import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import { queryClient } from '../../../queryClient';
import { usersOrgsPageQueryKey } from '../../../queryCacheScope';
import { createPortal } from 'react-dom';
import './users-orgs.css';
import '../../assets/assets.css';
import { listUsersAdmin, createUserAdmin, updateUserAdmin, deleteUserAdmin, resendUserInvitation, listOrganizations, createOrganization, updateOrganization, deleteOrganization, createSite, updateSite, verifyProfileCurrentPassword } from '../../../services/user.service';
import { listOrganizationsForSelector, getSites } from '../../../services/asset.service';
import { toUserFacingErrorMessage } from '../../../services/_core';
import {
  compareDates,
  compareLocaleStrings,
  compareNumbers,
  getTableSortIcon,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  getUsersOrgsTableColumns,
  USERS_ORGS_USER_TABLE_COLUMN_WIDTHS_KEY,
  USERS_ORGS_ORG_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import { TrashIcon } from '../../../shared/components/TrashIcon';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import UserScopeAssignFields, {
  EMPTY_USER_SCOPE_FORM,
  buildUserScopeCreatePayload,
  hasUserScopeSelection,
} from './UserScopeAssignFields';

const USERS_ORGS_CACHE_MS = 3 * 60 * 1000;

const BULK_ORG_DELETE_PASSWORD_MIN_LEN = 8;

const UNIVERSAL_SEARCH_MAX_LEN = 500;

function sanitizeUniversalSearchInput(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, UNIVERSAL_SEARCH_MAX_LEN);
}

function normalizeUiRole(profile) {
  if (profile?.is_superuser === true) return 'superuser';

  const rawRole = profile?.role;
  const roleStr = String(
    typeof rawRole === 'string'
      ? rawRole
      : (rawRole?.name ?? rawRole?.label ?? rawRole?.role ?? rawRole ?? '')
  ).trim().toLowerCase();

  if (!roleStr) return 'viewer';
  if (roleStr === 'superuser' || roleStr === 'root') return 'superuser';
  if (roleStr === 'partner' || roleStr === '4') return 'partner';
  if (roleStr === 'admin' || roleStr === 'administrator' || roleStr === '1') return 'admin';
  if (roleStr === 'analyst' || roleStr === '2') return 'analyst';
  if (roleStr === 'viewer' || roleStr === '3') return 'viewer';
  return roleStr;
}

function isPermissionDeniedError(err) {
  const text = String(err?.message || '').toLowerCase();
  return (
    text.includes('403') ||
    text.includes('forbidden') ||
    text.includes('permission denied') ||
    text.includes('not permitted')
  );
}

function toSafeOrganizationActionError(err, fallbackMessage) {
  if (isPermissionDeniedError(err)) {
    return 'You do not have permission to perform this organization action.';
  }
  return toUserFacingErrorMessage(err?.message, fallbackMessage);
}

function getPreferredTimezoneDefault() {
  return 'Australia/Melbourne';
}

export const USER_DETAIL_ROLE_OPTIONS = [
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Analyst', label: 'Analyst' },
  { value: 'Viewer', label: 'Viewer' },
];

/** Map API/legacy role strings into the three UI roles. */
export function normalizeUserRoleForForm(role) {
  const m = {
    Admin: 'Administrator',
    Member: 'Analyst',
    Administrator: 'Administrator',
    Analyst: 'Analyst',
    Viewer: 'Viewer',
  };
  return m[role] ?? (USER_DETAIL_ROLE_OPTIONS.some((o) => o.value === role) ? role : 'Viewer');
}

function formatUsersOrgsTableDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const s = d
    .toLocaleString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(/,\s*(\d{2}:\d{2})/, ' $1');
  return s;
}

function usersOrgsOrgRowSearchText(row) {
  return [
    row.name,
    row.description ?? '',
    String(row.perimeterHosts),
    String(row.onPremHosts),
    formatUsersOrgsTableDate(row.createdAt),
  ]
    .join(' ')
    .toLowerCase();
}

function usersOrgsUserRowSearchText(row) {
  return [
    row.name,
    row.email ?? '',
    row.role ?? '',
    row.organizationName ?? '',
    String(row.perimeterHosts),
    String(row.onPremHosts),
    formatUsersOrgsTableDate(row.createdAt),
  ]
    .join(' ')
    .toLowerCase();
}

function usersOrgsUserRowKey(row) {
  const orgId = String(row?.orgId || 'no-org').trim() || 'no-org';
  const userId = String(row?.userId || row?.id || '').trim() || 'unknown-user';
  return `${orgId}:${userId}`;
}

export function normalizeUsersOrgsOrganizations(rows) {
  return rows.map((row, index) => {
    const uuId = String(row?.uu_id || '').trim();
    const rawId = row?.id;
    const numericId =
      rawId != null && String(rawId) !== uuId && Number.isFinite(Number(rawId))
        ? Number(rawId)
        : null;
    const id = uuId || String(rawId || row?.organization_id || `org-${index}`).trim();
    return {
      id,
      uuId: id,
      numericId,
      name: String(row?.name || row?.org_name || `Organization ${index + 1}`).trim(),
      perimeterHosts: Number(row?.perimeterHosts || row?.perimeter_hosts || row?.num_perimeter_net_IP_used || 0) || 0,
      onPremHosts: Number(row?.onPremHosts || row?.on_prem_hosts || row?.num_onprem_net_IP_used || 0) || 0,
      createdAt: row?.creation_time || row?.created_time || row?.createdAt || new Date().toISOString(),
      description: String(row?.description || '').trim(),
      contact: String(row?.contact || '').trim(),
      address: String(row?.address || '').trim(),
      managedBy: String(row?.managedBy || row?.managed_by || '').trim(),
      managedByName: String(row?.managedByName || row?.managed_by_name || '').trim(),
      ecommerceMode: Boolean(row?.ecommerceMode ?? row?.ecommerce_mode),
      webScanEnabled: Boolean(
        row?.webScanEnabled ?? row?.web_app_scan_enabled ?? row?.web_scan_enabled
      ),
      // Create-only (maps to setup_default); not an editable org property.
      perimeterWithScanner: Boolean(row?.perimeterWithScanner || row?.perimeter_with_scanner),
    };
  });
}

function normalizeUsersOrgsUsers(rows, organizationRows) {
  const orgNameById = new Map();
  organizationRows.forEach((org) => {
    const id = String(org?.id || '').trim();
    if (id) orgNameById.set(id, org?.name || id);
  });

  return rows.map((row, index) => {
    const orgId = String(
      row?.organization_id || row?.organization_uuid || row?.org_id || row?.org_uuid || row?.organization || ''
    ).trim();
    const userId = String(row?.uu_id || row?.id || row?.user_id || `user-${index}`).trim();
    const roleRaw = String(row?.role || '').trim();
    const roleMap = { '1': 'Administrator', '2': 'Analyst', '3': 'Viewer' };
    const normalizedRole = normalizeUserRoleForForm(roleMap[roleRaw] || roleRaw);

    return {
      id: userId,
      userId,
      isApiUser: true,
      name: String(row?.name || row?.display_name || row?.email || `User ${index + 1}`).trim(),
      email: String(row?.email || '').trim(),
      role: normalizedRole,
      password: '',
      perimeterHosts: Number(row?.perimeterHosts || row?.perimeter_hosts || 0) || 0,
      onPremHosts: Number(row?.onPremHosts || row?.on_prem_hosts || 0) || 0,
      createdAt: row?.created_time || row?.createdAt || new Date().toISOString(),
      orgId,
      organizationName: orgNameById.get(orgId) || orgId || String(row?.organization_name || '').trim() || 'Unassigned',
      allOrganizationsAccess: Boolean(row?.all_organizations_access),
      allSitesAccess: Boolean(row?.all_sites_access),
      organizationAccessUuids: Array.isArray(row?.organization_access_uuids)
        ? row.organization_access_uuids.map((value) => String(value))
        : [],
      siteAccessUuids: Array.isArray(row?.site_access_uuids)
        ? row.site_access_uuids.map((value) => String(value))
        : [],
    };
  });
}

/** Organizations settings: only Admin / Administrator (or role id `1`) may delete organizations. */
function isUsersOrgsOrganizationAdminRole(role) {
  const r = String(role ?? '').trim().toLowerCase();
  if (!r) return false;
  if (r === '1') return true;
  if (r === 'admin' || r === 'administrator') return true;
  return false;
}

/** Settings  Organizations and Users: ALL ORGANIZATIONS / ALL USERS tabs (wire to admin API when ready). */
function UsersOrgsSettingsPage({ userProfile = {} }) {
  const usersOrgsSearchInputId = useId();
  const orgDetailManagedById = useId();
  const orgDetailNameId = useId();
  const orgDetailDescId = useId();
  const orgDetailContactId = useId();
  const orgDetailAddressId = useId();
  const orgDetailCbEcommerceId = useId();
  const orgDetailCbWebScanId = useId();
  const userDetailNameId = useId();
  const userDetailEmailId = useId();
  const userDetailPasswordId = useId();
  const userDetailPasswordHintId = useId();
  const userDetailOrgId = useId();
  const userDetailOrgSearchInputId = useId();
  const userDetailRoleId = useId();
  const userDetailOrgPickerRef = useRef(null);
  const bulkUsersSelectAllRef = useRef(null);
  const bulkOrgsSelectAllRef = useRef(null);
  const [organizationRows, setOrganizationRows] = useState(
    () => queryClient.getQueryData(usersOrgsPageQueryKey())?.organizationRows || []
  );
  const [apiUserRows, setApiUserRows] = useState(
    () => queryClient.getQueryData(usersOrgsPageQueryKey())?.apiUserRows || []
  );
  const [usersOrgsLoading, setUsersOrgsLoading] = useState(
    () => queryClient.getQueryData(usersOrgsPageQueryKey()) == null
  );
  const [usersOrgsError, setUsersOrgsError] = useState('');
  const [tab] = useState('organizations');
  const [orgsHeaderActionsHost, setOrgsHeaderActionsHost] = useState(null);

  useLayoutEffect(() => {
    setOrgsHeaderActionsHost(document.getElementById('orgs-page-header-actions'));
  }, []);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [organizationsSiteFilterId, setOrganizationsSiteFilterId] = useState('');
  const [organizationsSiteFilterOpen, setOrganizationsSiteFilterOpen] = useState(false);
  const [organizationsSiteFilterSearch, setOrganizationsSiteFilterSearch] = useState('');
  const organizationsSiteFilterRef = useRef(null);
  const [organizationSiteRows, setOrganizationSiteRows] = useState(
    () => queryClient.getQueryData(usersOrgsPageQueryKey())?.organizationSiteRows || []
  );
  const [organizationsSiteFilterSupported, setOrganizationsSiteFilterSupported] = useState(
    () => queryClient.getQueryData(usersOrgsPageQueryKey())?.organizationsSiteFilterSupported ?? true
  );
  const [orgDetailOpen, setOrgDetailOpen] = useState(false);
  const [orgDetailEditingId, setOrgDetailEditingId] = useState(null);
  const [orgDetailForm, setOrgDetailForm] = useState({
    name: '',
    description: '',
    contact: '',
    address: '',
    managedBy: '',
    ecommerceMode: false,
    webScanEnabled: false,
  });
  /** Pending delete: set when user clicks trash; confirmed in modal (replaces `window.confirm`). */
  const [orgDeleteConfirm, setOrgDeleteConfirm] = useState(null);
  const orgDeletePasswordConfirmId = useId();
  const orgDeletePasswordHintId = useId();
  const [orgDeletePasswordConfirm, setOrgDeletePasswordConfirm] = useState('');
  const [orgDeleteVerifying, setOrgDeleteVerifying] = useState(false);
  const [orgDeleteAuthError, setOrgDeleteAuthError] = useState('');
  const [userDeleteConfirm, setUserDeleteConfirm] = useState(null);
  const [deletedUserKeys, setDeletedUserKeys] = useState([]);
  const [userOverrides, setUserOverrides] = useState({});
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userDetailKey, setUserDetailKey] = useState(null);
  const [userPasswordResetStatus, setUserPasswordResetStatus] = useState('');
  const [userDetailOrgPickerOpen, setUserDetailOrgPickerOpen] = useState(false);
  const [userDetailOrgSearch, setUserDetailOrgSearch] = useState('');
  const [userDetailForm, setUserDetailForm] = useState({
    name: '',
    email: '',
    password: '',
    organization: '',
    organizationId: '',
    role: '',
  });
  const [selectedUserKeys, setSelectedUserKeys] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [openOrgRowMenuKey, setOpenOrgRowMenuKey] = useState(null);
  const [orgRowMenuPlacement, setOrgRowMenuPlacement] = useState(null);
  const orgRowActionsMenuRef = useRef(null);
  const orgRowActionsTriggerRef = useRef(null);
  const [bulkUserDeleteKeys, setBulkUserDeleteKeys] = useState(null);
  const [bulkOrgDeleteIds, setBulkOrgDeleteIds] = useState(null);
  const bulkOrgDeletePasswordConfirmId = useId();
  const bulkOrgDeletePasswordHintId = useId();
  const [bulkOrgDeletePasswordConfirm, setBulkOrgDeletePasswordConfirm] = useState('');
  const [bulkOrgDeleteVerifying, setBulkOrgDeleteVerifying] = useState(false);
  const [bulkOrgDeleteAuthError, setBulkOrgDeleteAuthError] = useState('');
  /** Users created from Add/Invite modal before list refresh. */
  const [adHocUserRows, setAdHocUserRows] = useState([]);
  const [addInviteUserModalOpen, setAddInviteUserModalOpen] = useState(false);
  /** 'choice' | 'add' | 'invite' */
  const [addInviteUserStep, setAddInviteUserStep] = useState('choice');
  const [addUserModalForm, setAddUserModalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'Viewer',
    ...EMPTY_USER_SCOPE_FORM,
  });
  const [inviteUserModalForm, setInviteUserModalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Viewer',
    ...EMPTY_USER_SCOPE_FORM,
  });
  const [addUserOrgSearch, setAddUserOrgSearch] = useState('');
  const [addUserSiteSearch, setAddUserSiteSearch] = useState('');
  const [inviteUserOrgSearch, setInviteUserOrgSearch] = useState('');
  const [inviteUserSiteSearch, setInviteUserSiteSearch] = useState('');
  const [addUserFieldErrors, setAddUserFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [inviteUserFieldErrors, setInviteUserFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [addInviteUserModalError, setAddInviteUserModalError] = useState('');
  const [addOrganizationModalOpen, setAddOrganizationModalOpen] = useState(false);
  const [addOrganizationForm, setAddOrganizationForm] = useState({
    name: '',
    description: '',
    managedBy: '',
    ecommerceMode: true,
    webScanEnabled: false,
    perimeterWithScanner: true,
    assignedUserIds: [],
  });
  const [addOrganizationSubmitting, setAddOrganizationSubmitting] = useState(false);
  const [addOrganizationModalError, setAddOrganizationModalError] = useState('');
  const [addOrganizationUserSearch, setAddOrganizationUserSearch] = useState('');
  const addUserFormNameId = useId();
  const addUserFormLastNameId = useId();
  const addUserFormEmailId = useId();
  const addUserFormPasswordId = useId();
  const addUserFormOrgSearchId = useId();
  const addUserFormSiteSearchId = useId();
  const addUserFormRoleId = useId();
  const inviteUserFormNameId = useId();
  const inviteUserFormLastNameId = useId();
  const inviteUserFormEmailId = useId();
  const inviteUserFormOrgSearchId = useId();
  const inviteUserFormSiteSearchId = useId();
  const inviteUserFormRoleId = useId();
  const addOrganizationNameId = useId();
  const addOrganizationUsersId = useId();
  const addOrganizationDescId = useId();
  const addOrganizationManagedById = useId();
  const addOrganizationCbEcommerceId = useId();
  const addOrganizationCbWebScanId = useId();
  const addOrganizationCbPerimeterId = useId();

  const isUsersTab = tab === 'users';
  const usersOrgsRole = normalizeUiRole(userProfile || {});
  const isUsersOrgsSuperuser = usersOrgsRole === 'superuser';
  const isUsersOrgsStaffAdmin = usersOrgsRole === 'admin' && String(userProfile?.organization_id || '').trim() === '1';
  const canEditOrgPlatformFields = isUsersOrgsSuperuser || isUsersOrgsStaffAdmin;

  const canManageOrganizations = useMemo(() => {
    const roleValue = String(userProfile?.role ?? '').trim().toLowerCase();
    const caps = userProfile?.capabilities || {};
    const effectiveCaps = caps?.effective_capabilities || {};
    return (
      isUsersOrgsOrganizationAdminRole(userProfile?.role) ||
      isUsersOrgsSuperuser ||
      isUsersOrgsStaffAdmin ||
      roleValue === 'partner' ||
      caps?.can_manage_organizations === true ||
      effectiveCaps?.can_manage_organizations === true
    );
  }, [userProfile?.role, userProfile?.capabilities, isUsersOrgsSuperuser, isUsersOrgsStaffAdmin]);

  const canCreateOrganizations = canManageOrganizations;
  const canDeleteOrganizations = canManageOrganizations;

  useEffect(() => {
    if (canDeleteOrganizations) return;
    setSelectedOrgIds([]);
    setBulkOrgDeleteIds(null);
    setBulkOrgDeletePasswordConfirm('');
    setBulkOrgDeleteAuthError('');
    setBulkOrgDeleteVerifying(false);
    setOrgDeleteConfirm(null);
    setOrgDeletePasswordConfirm('');
    setOrgDeleteAuthError('');
    setOrgDeleteVerifying(false);
  }, [canDeleteOrganizations]);

  const loadUsersOrgsData = useCallback(async ({ fresh = false, includeUsers = false } = {}) => {
    const cached = queryClient.getQueryData(usersOrgsPageQueryKey());
    if (cached && !fresh) {
      setOrganizationRows(cached.organizationRows || []);
      setApiUserRows(cached.apiUserRows || []);
      setOrganizationSiteRows(cached.organizationSiteRows || []);
      setOrganizationsSiteFilterSupported(cached.organizationsSiteFilterSupported !== false);
      setUsersOrgsLoading(false);
      const updatedAt = queryClient.getQueryState(usersOrgsPageQueryKey())?.dataUpdatedAt || 0;
      if (Date.now() - updatedAt < USERS_ORGS_CACHE_MS) return;
    } else if (!cached) {
      setUsersOrgsLoading(true);
    }
    setUsersOrgsError('');
    try {
      const needUsers =
        includeUsers ||
        fresh ||
        Boolean(cached?.apiUserRows?.length);
      const [orgResult, sitesResult, usersResult] = await Promise.allSettled([
        listOrganizations(),
        getSites(),
        needUsers ? listUsersAdmin() : Promise.resolve(null),
      ]);

      const orgRowsRaw = orgResult.status === 'fulfilled' && Array.isArray(orgResult.value)
        ? orgResult.value
        : [];
      const normalizedOrgs = normalizeUsersOrgsOrganizations(orgRowsRaw);
      setOrganizationRows(normalizedOrgs);

      let normalizedUsers = cached?.apiUserRows || [];
      if (needUsers && usersResult.status === 'fulfilled' && Array.isArray(usersResult.value)) {
        normalizedUsers = normalizeUsersOrgsUsers(usersResult.value, normalizedOrgs);
        setApiUserRows(normalizedUsers);
      } else if (!needUsers) {
        setApiUserRows(normalizedUsers);
      } else if (usersResult.status === 'rejected') {
        setApiUserRows([]);
      }

      const sitesRowsRaw = sitesResult.status === 'fulfilled' && Array.isArray(sitesResult.value)
        ? sitesResult.value
        : [];
      const normalizedSites = sitesRowsRaw
        .map((site, idx) => {
          const id = String(site?.uu_id || site?.site_uuid || site?.id || `site-${idx}`).trim();
          const name = String(site?.site_name || site?.name || site?.site || '').trim();
          const orgId = String(
            site?.org_uuid || site?.organization_uuid || site?.organization_uu_id || site?.organization_id || site?.org_id || ''
          ).trim();
          const orgName = String(site?.organization_name || site?.organization || '').trim();
          if (!id || !name) return null;
          return { id, name, orgId, orgName };
        })
        .filter(Boolean);
      setOrganizationSiteRows(normalizedSites);
      setOrganizationsSiteFilterSupported(sitesResult.status === 'fulfilled');

      const orgsOk = orgResult.status === 'fulfilled';
      const sitesOk = sitesResult.status === 'fulfilled';
      const usersOk = !needUsers || usersResult.status === 'fulfilled';
      if (orgsOk && sitesOk && usersOk) {
        queryClient.setQueryData(usersOrgsPageQueryKey(), {
          organizationRows: normalizedOrgs,
          apiUserRows: normalizedUsers,
          organizationSiteRows: normalizedSites,
          organizationsSiteFilterSupported: true,
        });
      }

      if (orgResult.status === 'rejected' || sitesResult.status === 'rejected' || (needUsers && usersResult.status === 'rejected')) {
        setUsersOrgsError('Some users or organizations could not be loaded. Showing partial data.');
      }
    } catch (err) {
      setUsersOrgsError(
        toUserFacingErrorMessage(err?.message, 'Failed to load users and organizations.')
      );
      setOrganizationSiteRows([]);
      setOrganizationsSiteFilterSupported(false);
    } finally {
      setUsersOrgsLoading(false);
    }
  }, []);

  const loadAssignUsersIfNeeded = useCallback(async () => {
    if (apiUserRows.length > 0) return;
    const cached = queryClient.getQueryData(usersOrgsPageQueryKey());
    if (Array.isArray(cached?.apiUserRows) && cached.apiUserRows.length > 0) {
      setApiUserRows(cached.apiUserRows);
      return;
    }
    try {
      const usersRowsRaw = await listUsersAdmin();
      const normalizedUsers = normalizeUsersOrgsUsers(
        Array.isArray(usersRowsRaw) ? usersRowsRaw : [],
        organizationRows
      );
      setApiUserRows(normalizedUsers);
      const prev = queryClient.getQueryData(usersOrgsPageQueryKey()) || {};
      queryClient.setQueryData(usersOrgsPageQueryKey(), {
        ...prev,
        organizationRows: prev.organizationRows || organizationRows,
        organizationSiteRows: prev.organizationSiteRows || organizationSiteRows,
        organizationsSiteFilterSupported: prev.organizationsSiteFilterSupported !== false,
        apiUserRows: normalizedUsers,
      });
    } catch (_) {
      // Assign picker shows empty state; org table already rendered.
    }
  }, [apiUserRows.length, organizationRows, organizationSiteRows]);

  useEffect(() => {
    loadUsersOrgsData({ includeUsers: false });
  }, [loadUsersOrgsData]);

  useEffect(() => {
    if (!addOrganizationModalOpen) return;
    loadAssignUsersIfNeeded();
  }, [addOrganizationModalOpen, loadAssignUsersIfNeeded]);

  const allUsersRows = useMemo(() => {
    const base = apiUserRows.length > 0 ? apiUserRows : [];
    const merged = [...base, ...adHocUserRows];
    const del = new Set(deletedUserKeys);
    return merged
      .filter((r) => !del.has(usersOrgsUserRowKey(r)))
      .map((r) => {
        const k = usersOrgsUserRowKey(r);
        const o = userOverrides[k];
        return o ? { ...r, ...o } : r;
      });
  }, [apiUserRows, organizationRows, deletedUserKeys, userOverrides, adHocUserRows]);

  const addOrganizationUserMatches = useMemo(() => {
    const query = addOrganizationUserSearch.trim().toLowerCase();
    if (!query) return allUsersRows;
    return allUsersRows.filter((user) => {
      const name = String(user.name || '').toLowerCase();
      const email = String(user.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [allUsersRows, addOrganizationUserSearch]);

  const userDetailOrgPickerFiltered = useMemo(() => {
    const q = userDetailOrgSearch.trim().toLowerCase();
    if (!q) return organizationRows;
    return organizationRows.filter((o) => o.name.toLowerCase().includes(q));
  }, [organizationRows, userDetailOrgSearch]);

  const managedByOrganizationOptions = useMemo(
    () =>
      organizationRows
        .map((org) => {
          const value = String(org?.id || '').trim();
          const label = String(org?.name || value).trim();
          return value ? { value, label } : null;
        })
        .filter(Boolean),
    [organizationRows]
  );

  const orgDetailManagedByOptions = useMemo(() => {
    const editingId = String(orgDetailEditingId || '').trim();
    const options = managedByOrganizationOptions.filter((opt) => opt.value !== editingId);
    const current = String(orgDetailForm.managedBy || '').trim();
    if (current && !options.some((opt) => opt.value === current)) {
      const editingRow = organizationRows.find((o) => o.id === editingId);
      options.unshift({
        value: current,
        label: editingRow?.managedByName || current,
      });
    }
    return options;
  }, [
    managedByOrganizationOptions,
    orgDetailEditingId,
    orgDetailForm.managedBy,
    organizationRows,
  ]);

  useEffect(() => {
    if (!userDetailOrgPickerOpen) return;
    const onDoc = (e) => {
      const el = userDetailOrgPickerRef.current;
      if (el && !el.contains(e.target)) setUserDetailOrgPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [userDetailOrgPickerOpen]);

  useEffect(() => {
    if (!organizationsSiteFilterOpen) return;
    const onDoc = (e) => {
      const el = organizationsSiteFilterRef.current;
      if (el && !el.contains(e.target)) {
        setOrganizationsSiteFilterOpen(false);
        setOrganizationsSiteFilterSearch('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [organizationsSiteFilterOpen]);

  const organizationsSiteFilterActiveName = useMemo(() => {
    if (!organizationsSiteFilterId) return '';
    const selectedId = String(organizationsSiteFilterId).trim();
    return organizationSiteRows.find((s) => String(s.id) === selectedId)?.name ?? '';
  }, [organizationSiteRows, organizationsSiteFilterId]);

  const organizationsSiteFilterList = useMemo(() => {
    const q = organizationsSiteFilterSearch.trim().toLowerCase();
    if (!q) return organizationSiteRows;
    return organizationSiteRows.filter((s) => {
      const name = String(s.name || '').toLowerCase();
      const orgName = String(s.orgName || '').toLowerCase();
      const id = String(s.id || '').toLowerCase();
      return name.includes(q) || orgName.includes(q) || id.includes(q);
    });
  }, [organizationSiteRows, organizationsSiteFilterSearch]);

  const closeAddInviteUserModal = useCallback(() => {
    setAddInviteUserModalOpen(false);
    setAddInviteUserStep('choice');
    setAddInviteUserModalError('');
    setAddUserOrgSearch('');
    setAddUserSiteSearch('');
    setInviteUserOrgSearch('');
    setInviteUserSiteSearch('');
    setAddUserFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
    setInviteUserFieldErrors({ firstName: '', lastName: '', email: '' });
    setAddUserModalForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'Viewer',
      ...EMPTY_USER_SCOPE_FORM,
    });
    setInviteUserModalForm({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Viewer',
      ...EMPTY_USER_SCOPE_FORM,
    });
  }, []);

  const submitAddUserModal = useCallback(
    async (e) => {
      e.preventDefault();
      const firstName = addUserModalForm.firstName.trim();
      const lastName = addUserModalForm.lastName.trim();
      const name = [firstName, lastName].filter(Boolean).join(' ');
      const email = addUserModalForm.email.trim();
      const password = addUserModalForm.password.trim();
      const hasAnyText = Boolean(firstName || lastName || email || password);
      if (!hasAnyText) return;

      const nextFieldErrors = {
        firstName: firstName ? '' : 'Required',
        lastName: lastName ? '' : 'Required',
        email: email ? '' : 'Required',
        password: password ? '' : 'Required',
      };
      if (nextFieldErrors.firstName || nextFieldErrors.lastName || nextFieldErrors.email || nextFieldErrors.password) {
        setAddUserFieldErrors(nextFieldErrors);
        requestAnimationFrame(() => {
          const focusId = !firstName
            ? addUserFormNameId
            : !lastName
              ? addUserFormLastNameId
              : !email
                ? addUserFormEmailId
                : addUserFormPasswordId;
          const el = document.getElementById(focusId);
          el?.focus?.();
          el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        });
        return;
      }

      if (!hasUserScopeSelection(addUserModalForm)) {
        setAddInviteUserModalError('Select at least one organization, or enable all organizations.');
        return;
      }
      const scopePayload = buildUserScopeCreatePayload(addUserModalForm, organizationRows);
      if (!scopePayload.organization) {
        setAddInviteUserModalError('Select at least one organization.');
        return;
      }
      setAddInviteUserModalError('');
      setAddUserFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
      try {
        await createUserAdmin({
          name,
          email,
          action: 'add',
          password,
          role: addUserModalForm.role,
          timezone: getPreferredTimezoneDefault(),
          ...scopePayload,
        });
        await loadUsersOrgsData({ fresh: true });
        closeAddInviteUserModal();
      } catch (err) {
        setAddInviteUserModalError(
          toUserFacingErrorMessage(err?.message, `Failed to create user ${email}.`)
        );
      }
    },
    [
      addUserModalForm,
      organizationRows,
      closeAddInviteUserModal,
      loadUsersOrgsData,
      addUserFormNameId,
      addUserFormLastNameId,
      addUserFormEmailId,
      addUserFormPasswordId,
    ]
  );

  const submitInviteUserModal = useCallback(
    async (e) => {
      e.preventDefault();
      const firstName = inviteUserModalForm.firstName.trim();
      const lastName = inviteUserModalForm.lastName.trim();
      const name = [firstName, lastName].filter(Boolean).join(' ');
      const email = inviteUserModalForm.email.trim();
      const hasAnyText = Boolean(firstName || lastName || email);
      if (!hasAnyText) return;

      const nextFieldErrors = {
        firstName: firstName ? '' : 'Required',
        lastName: lastName ? '' : 'Required',
        email: email ? '' : 'Required',
      };
      if (nextFieldErrors.firstName || nextFieldErrors.lastName || nextFieldErrors.email) {
        setInviteUserFieldErrors(nextFieldErrors);
        requestAnimationFrame(() => {
          const focusId = !firstName
            ? inviteUserFormNameId
            : !lastName
              ? inviteUserFormLastNameId
              : inviteUserFormEmailId;
          const el = document.getElementById(focusId);
          el?.focus?.();
          el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        });
        return;
      }

      if (!hasUserScopeSelection(inviteUserModalForm)) {
        setAddInviteUserModalError('Select at least one organization, or enable all organizations.');
        return;
      }
      const scopePayload = buildUserScopeCreatePayload(inviteUserModalForm, organizationRows);
      if (!scopePayload.organization) {
        setAddInviteUserModalError('Select at least one organization.');
        return;
      }
      setAddInviteUserModalError('');
      setInviteUserFieldErrors({ firstName: '', lastName: '', email: '' });
      try {
        await createUserAdmin({
          name,
          email,
          action: 'invite',
          role: inviteUserModalForm.role,
          timezone: getPreferredTimezoneDefault(),
          ...scopePayload,
        });
        await loadUsersOrgsData({ fresh: true });
        closeAddInviteUserModal();
      } catch (err) {
        setAddInviteUserModalError(
          toUserFacingErrorMessage(err?.message, `Failed to invite user ${email}.`)
        );
      }
    },
    [
      inviteUserModalForm,
      organizationRows,
      closeAddInviteUserModal,
      loadUsersOrgsData,
      inviteUserFormNameId,
      inviteUserFormLastNameId,
      inviteUserFormEmailId,
    ]
  );

  const closeAddOrganizationModal = useCallback(() => {
    setAddOrganizationModalOpen(false);
    setAddOrganizationForm({
      name: '',
      description: '',
      managedBy: '',
      ecommerceMode: true,
      webScanEnabled: false,
      perimeterWithScanner: true,
      assignedUserIds: [],
    });
    setAddOrganizationModalError('');
    setAddOrganizationSubmitting(false);
    setAddOrganizationUserSearch('');
  }, []);

  const handleAddOrganization = useCallback(() => {
    if (!canCreateOrganizations) {
      setUsersOrgsError('You do not have permission to create organizations.');
      return;
    }
    setAddOrganizationForm({
      name: '',
      description: '',
      managedBy: '',
      ecommerceMode: true,
      webScanEnabled: false,
      perimeterWithScanner: true,
      assignedUserIds: [],
    });
    setAddOrganizationModalError('');
    setAddOrganizationUserSearch('');
    setAddOrganizationModalOpen(true);
  }, [canCreateOrganizations]);

  const submitAddOrganizationModal = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canCreateOrganizations) {
        setAddOrganizationModalError('You do not have permission to create organizations.');
        return;
      }
      const name = String(addOrganizationForm.name || '').trim();
      if (!name) return;
      setAddOrganizationSubmitting(true);
      setAddOrganizationModalError('');
      try {
        const payload = {
          name,
          org_name: name,
          description: String(addOrganizationForm.description || '').trim(),
          setup_default: Boolean(addOrganizationForm.perimeterWithScanner),
          ecommerce_mode: Boolean(addOrganizationForm.ecommerceMode),
          web_app_scan_enabled: Boolean(addOrganizationForm.webScanEnabled),
        };
        const managedBy = String(addOrganizationForm.managedBy || '').trim();
        if (managedBy) payload.managed_by = managedBy;

        const created = await createOrganization(payload);
        const orgUuid = String(created?.uu_id || created?.uuid || '').trim();
        const selectedIds = Array.isArray(addOrganizationForm.assignedUserIds)
          ? addOrganizationForm.assignedUserIds
          : [];
        const failedNames = [];
        if (orgUuid && selectedIds.length) {
          for (const userId of selectedIds) {
            const user = allUsersRows.find((row) => String(row.userId || row.id) === String(userId));
            if (!user || user.allOrganizationsAccess) continue;
            const existing = Array.isArray(user.organizationAccessUuids) ? user.organizationAccessUuids : [];
            const organizationAccessUuids = Array.from(new Set([...existing.map(String), orgUuid]));
            try {
              await updateUserAdmin({
                uu_id: user.userId || user.id,
                all_organizations_access: false,
                all_sites_access: Boolean(user.allSitesAccess),
                organization_access_uuids: organizationAccessUuids,
                site_access_uuids: Array.isArray(user.siteAccessUuids) ? user.siteAccessUuids : [],
              });
            } catch {
              failedNames.push(user.name || user.email || 'A user');
            }
          }
        }
        await loadUsersOrgsData({ fresh: true });
        if (failedNames.length) {
          setAddOrganizationModalError(
            `Organization created, but these users could not be assigned: ${failedNames.join(', ')}.`
          );
          return;
        }
        closeAddOrganizationModal();
      } catch (err) {
        setAddOrganizationModalError(toSafeOrganizationActionError(err, 'Failed to create organization.'));
      } finally {
        setAddOrganizationSubmitting(false);
      }
    },
    [addOrganizationForm, allUsersRows, canCreateOrganizations, loadUsersOrgsData, closeAddOrganizationModal]
  );

  const sourceRows = isUsersTab ? allUsersRows : organizationRows;
  const searchQuery = isUsersTab ? usersSearchQuery : orgSearchQuery;
  const setSearchQuery = isUsersTab ? setUsersSearchQuery : setOrgSearchQuery;
  const searchPlaceholder = isUsersTab ? 'Search users' : 'Search organizations';

  useEffect(() => {
    setSortConfig({ key: 'name', direction: 'asc' });
  }, [tab]);

  useEffect(() => {
    setSelectedUserKeys([]);
    setSelectedOrgIds([]);
    setBulkUserDeleteKeys(null);
    setBulkOrgDeleteIds(null);
  }, [tab]);

  const filteredSourceRows = useMemo(() => {
    let rows = sourceRows;
    if (!isUsersTab && organizationsSiteFilterId) {
      const selectedSiteId = String(organizationsSiteFilterId).trim();
      const selectedSite = organizationSiteRows.find((s) => String(s.id).trim() === selectedSiteId);
      if (selectedSite) {
        const selectedOrgId = String(selectedSite.orgId || '').trim();
        const selectedOrgName = String(selectedSite.orgName || '').trim().toLowerCase();
        rows = rows.filter((row) => {
          const rowOrgId = String(row.id || '').trim();
          const rowName = String(row.name || '').trim().toLowerCase();
          if (selectedOrgId && rowOrgId === selectedOrgId) return true;
          if (selectedOrgName && rowName === selectedOrgName) return true;
          return false;
        });
      }
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    if (isUsersTab) {
      return rows.filter((row) => usersOrgsUserRowSearchText(row).includes(q));
    }
    return rows.filter((row) => usersOrgsOrgRowSearchText(row).includes(q));
  }, [sourceRows, isUsersTab, searchQuery, organizationsSiteFilterId, organizationSiteRows]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredSourceRows];
    const { key, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (key === 'name') return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * factor;
      if (key === 'organization') {
        const an = a.organizationName ?? '';
        const bn = b.organizationName ?? '';
        return an.localeCompare(bn, undefined, { sensitivity: 'base' }) * factor;
      }
      if (key === 'perimeterHosts') return (a.perimeterHosts - b.perimeterHosts) * factor;
      if (key === 'onPremHosts') return (a.onPremHosts - b.onPremHosts) * factor;
      if (key === 'createdAt') return (new Date(a.createdAt) - new Date(b.createdAt)) * factor;
      return 0;
    });
    return rows;
  }, [filteredSourceRows, sortConfig]);

  const tablePagination = usePaginatedRows(sortedRows, TABLE_PAGE_SIZE);
  const visibleRows = tablePagination.pageRows;

  const selectedUserKeysAllVisible =
    isUsersTab &&
    visibleRows.length > 0 &&
    visibleRows.every((r) => selectedUserKeys.includes(usersOrgsUserRowKey(r)));

  const selectedOrgIdsAllVisible =
    !isUsersTab &&
    visibleRows.length > 0 &&
    visibleRows.every((r) => selectedOrgIds.includes(r.id));

  const toggleSelectAllVisibleUsers = useCallback(() => {
    setSelectedUserKeys((prev) => {
      const visible = visibleRows.map((r) => usersOrgsUserRowKey(r));
      const all = prev.length > 0 && visible.every((k) => prev.includes(k));
      return all ? prev.filter((k) => !visible.includes(k)) : [...new Set([...prev, ...visible])];
    });
  }, [visibleRows]);

  const toggleSelectAllVisibleOrgs = useCallback(() => {
    if (!canDeleteOrganizations) return;
    setSelectedOrgIds((prev) => {
      const visible = visibleRows.map((r) => r.id);
      const all = prev.length > 0 && visible.every((id) => prev.includes(id));
      return all ? prev.filter((id) => !visible.includes(id)) : [...new Set([...prev, ...visible])];
    });
  }, [visibleRows, canDeleteOrganizations]);

  const toggleUserRowSelected = useCallback((row) => {
    const k = usersOrgsUserRowKey(row);
    setSelectedUserKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }, []);

  const toggleOrgRowSelected = useCallback((row) => {
    if (!canDeleteOrganizations) return;
    setSelectedOrgIds((prev) => (prev.includes(row.id) ? prev.filter((x) => x !== row.id) : [...prev, row.id]));
  }, [canDeleteOrganizations]);

  const allVisibleUsersSelected =
    isUsersTab &&
    visibleRows.length > 0 &&
    visibleRows.every((r) => selectedUserKeys.includes(usersOrgsUserRowKey(r)));

  const allVisibleOrgsSelected =
    !isUsersTab &&
    visibleRows.length > 0 &&
    visibleRows.every((r) => selectedOrgIds.includes(r.id));

  const toggleSort = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  }, []);

  const sortIcon = useCallback((key) => getTableSortIcon(sortConfig, key), [sortConfig]);

  const usersOrgsTableColumns = useMemo(() => {
    const cols = getUsersOrgsTableColumns({ isUsersTab }).map((col) => ({ ...col }));
    const checkboxCol = cols.find((col) => col.key === 'checkbox');
    if (checkboxCol) {
      checkboxCol.headerContent = isUsersTab ? (
        <input
          ref={bulkUsersSelectAllRef}
          type="checkbox"
          className="users-orgs-settings-checkbox"
          checked={allVisibleUsersSelected}
          disabled={sortedRows.length === 0}
          onChange={toggleSelectAllVisibleUsers}
          aria-label="Select all users in this list"
        />
      ) : (
        <input
          ref={bulkOrgsSelectAllRef}
          type="checkbox"
          className="users-orgs-settings-checkbox"
          checked={allVisibleOrgsSelected}
          disabled={sortedRows.length === 0 || !canDeleteOrganizations}
          onChange={toggleSelectAllVisibleOrgs}
          aria-label="Select all organizations in this list"
          title={!canDeleteOrganizations ? 'Only administrators can delete organizations.' : undefined}
        />
      );
    }
    return cols;
  }, [
    isUsersTab,
    allVisibleUsersSelected,
    allVisibleOrgsSelected,
    sortedRows.length,
    canDeleteOrganizations,
    toggleSelectAllVisibleUsers,
    toggleSelectAllVisibleOrgs,
  ]);

  const {
    widths: usersOrgsColumnWidths,
    onResizeStart: onUsersOrgsColumnResizeStart,
    tableMinWidth: usersOrgsTableMinWidth,
    resizingKey: usersOrgsColumnResizingKey,
  } = useTableColumnResize(
    usersOrgsTableColumns,
    isUsersTab ? USERS_ORGS_USER_TABLE_COLUMN_WIDTHS_KEY : USERS_ORGS_ORG_TABLE_COLUMN_WIDTHS_KEY
  );

  const usersOrgsTableHeaderRef = useRef(null);
  const usersOrgsTableScrollRef = useRef(null);
  const [usersOrgsHeaderGutterPx, setUsersOrgsHeaderGutterPx] = useState(8);

  const handleUsersOrgsTableScroll = useCallback((event) => {
    if (usersOrgsTableHeaderRef.current) {
      usersOrgsTableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = usersOrgsTableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;
    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setUsersOrgsHeaderGutterPx((prev) => (prev === next ? prev : next));
    };
    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [isUsersTab, usersOrgsLoading, sortedRows.length]);

  const usersOrgsTableClassName = `assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table users-orgs-settings-table${
    isUsersTab ? ' users-orgs-settings-table--all-users' : ''
  }`;
  const usersOrgsTableStyle = { width: '100%', minWidth: usersOrgsTableMinWidth };

  const emptyMessage = useMemo(() => {
    if (usersOrgsError && sourceRows.length === 0) {
      return isUsersTab ? 'Unable to load users.' : 'Unable to load organizations.';
    }
    if (sourceRows.length === 0) {
      return isUsersTab ? 'No users yet.' : 'No organizations yet.';
    }
    if (filteredSourceRows.length === 0) {
      return 'No matches for your search.';
    }
    return '';
  }, [
    usersOrgsError,
    sourceRows.length,
    filteredSourceRows.length,
    isUsersTab,
  ]);

  const openOrgDetail = useCallback((orgRow) => {
    setOrgDetailEditingId(orgRow.id);
    setOrgDetailForm({
      name: orgRow.name,
      description: orgRow.description ?? '',
      contact: orgRow.contact ?? '',
      address: orgRow.address ?? '',
      managedBy: orgRow.managedBy ?? '',
      ecommerceMode: !!orgRow.ecommerceMode,
      webScanEnabled: !!orgRow.webScanEnabled,
    });
    setOrgDetailOpen(true);
  }, []);

  const closeOrgDetail = useCallback(() => {
    setOrgDetailOpen(false);
    setOrgDetailEditingId(null);
  }, []);

  const closeOrgRowActionsMenu = useCallback(() => {
    setOpenOrgRowMenuKey(null);
    setOrgRowMenuPlacement(null);
  }, []);

  useEffect(() => {
    if (!openOrgRowMenuKey) return undefined;
    const onPointerDown = (event) => {
      const inMenu = orgRowActionsMenuRef.current?.contains(event.target);
      const inTrigger = orgRowActionsTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) closeOrgRowActionsMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeOrgRowActionsMenu();
    };
    const onRepositionClose = () => closeOrgRowActionsMenu();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onRepositionClose);
    window.addEventListener('scroll', onRepositionClose, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onRepositionClose);
      window.removeEventListener('scroll', onRepositionClose, true);
    };
  }, [openOrgRowMenuKey, closeOrgRowActionsMenu]);

  const requestDeleteOrganization = useCallback(
    (orgRow) => {
      if (!canDeleteOrganizations) {
        setUsersOrgsError('Only administrators can delete organizations.');
        return;
      }
      setOrgDeletePasswordConfirm('');
      setOrgDeleteAuthError('');
      setOrgDeleteVerifying(false);
      setOrgDeleteConfirm({ id: orgRow.id, name: orgRow.name });
    },
    [canDeleteOrganizations]
  );

  const closeDeleteOrganizationModal = useCallback(() => {
    setOrgDeleteConfirm(null);
    setOrgDeletePasswordConfirm('');
    setOrgDeleteAuthError('');
    setOrgDeleteVerifying(false);
  }, []);

  const handleOrgDetailSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!orgDetailEditingId) return;

      const payload = {
        uu_id: orgDetailEditingId,
        name: orgDetailForm.name.trim(),
        description: orgDetailForm.description,
        contact: orgDetailForm.contact,
        address: orgDetailForm.address,
      };

      if (canEditOrgPlatformFields) {
        const managedByUuid = String(orgDetailForm.managedBy || '').trim();
        payload.managed_by = managedByUuid || null;
        payload.ecommerce_mode = Boolean(orgDetailForm.ecommerceMode);
        payload.web_app_scan_enabled = Boolean(orgDetailForm.webScanEnabled);
      }

      try {
        const updated = await updateOrganization(payload);
        const next = normalizeUsersOrgsOrganizations(Array.isArray(updated) ? updated : [updated])[0];
        setOrganizationRows((rows) =>
          rows.map((r) =>
            r.id === orgDetailEditingId
              ? {
                  ...r,
                  ...(next || {
                    name: payload.name || r.name,
                    description: payload.description,
                    contact: payload.contact,
                    address: payload.address,
                    ...(canEditOrgPlatformFields
                      ? {
                          managedBy: orgDetailForm.managedBy,
                          managedByName:
                            organizationRows.find((o) => o.id === orgDetailForm.managedBy)?.name
                            || r.managedByName
                            || '',
                          ecommerceMode: orgDetailForm.ecommerceMode,
                          webScanEnabled: orgDetailForm.webScanEnabled,
                        }
                      : {}),
                  }),
                }
              : r
          )
        );
        await loadUsersOrgsData({ fresh: true });
        closeOrgDetail();
      } catch (err) {
        setUsersOrgsError(
          toUserFacingErrorMessage(err?.message, 'Failed to update organization.')
        );
      }
    },
    [orgDetailEditingId, orgDetailForm, organizationRows, closeOrgDetail, canEditOrgPlatformFields, loadUsersOrgsData]
  );

  const openUserDetail = useCallback((row) => {
    const k = usersOrgsUserRowKey(row);
    setUserDetailKey(k);
    setUserDetailForm({
      name: row.name,
      email: row.email ?? '',
      password: row.password ?? '',
      organization: row.organizationName ?? '',
      organizationId: row.orgId ?? '',
      role: normalizeUserRoleForForm(row.role),
    });
    setUserPasswordResetStatus('');
    setUserDetailOrgPickerOpen(false);
    setUserDetailOrgSearch('');
    setUserDetailOpen(true);
  }, []);

  const closeUserDetail = useCallback(() => {
    setUserDetailOpen(false);
    setUserDetailKey(null);
    setUserPasswordResetStatus('');
    setUserDetailOrgPickerOpen(false);
    setUserDetailOrgSearch('');
  }, []);

  const handleUserPasswordResetRequest = useCallback(() => {
    const email = userDetailForm.email.trim();
    setUserPasswordResetStatus(
      email
        ? `A reset link would be sent to ${email}. (Demo  not emailed.)`
        : 'Add an email address first, then request a reset.'
    );
  }, [userDetailForm.email]);

  const confirmDeleteOrganization = useCallback(async () => {
    if (!orgDeleteConfirm) return;
    if (!canDeleteOrganizations) return;
    if (orgDeletePasswordConfirm.trim().length < BULK_ORG_DELETE_PASSWORD_MIN_LEN) return;

    setOrgDeleteVerifying(true);
    setOrgDeleteAuthError('');
    try {
      await verifyProfileCurrentPassword(orgDeletePasswordConfirm);
    } catch (err) {
      setOrgDeleteAuthError(
        toUserFacingErrorMessage(err?.message, 'Password verification failed.')
      );
      setOrgDeleteVerifying(false);
      return;
    }

    const { id } = orgDeleteConfirm;
    try {
      await deleteOrganization({ uu_id: id });
    } catch (err) {
      setUsersOrgsError(toSafeOrganizationActionError(err, 'Failed to delete organization.'));
      setOrgDeleteVerifying(false);
      return;
    }

    if (orgDetailOpen && orgDetailEditingId === id) {
      closeOrgDetail();
    }
    if (userDetailOpen && userDetailKey && userDetailKey.startsWith(`${id}:`)) {
      closeUserDetail();
    }
    setUserOverrides((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (k.startsWith(`${id}:`)) delete next[k];
      }
      return next;
    });
    setDeletedUserKeys((prev) => prev.filter((k) => !k.startsWith(`${id}:`)));
    setSelectedOrgIds((prev) => prev.filter((oid) => oid !== id));
    setAdHocUserRows((rows) => rows.filter((r) => r.orgId !== id));
    setOrganizationRows((rows) => rows.filter((r) => r.id !== id));
    setOrganizationSiteRows((rows) =>
      rows.filter((s) => {
        const oid = String(s.orgId || '').trim();
        return !oid || oid !== String(id).trim();
      })
    );
    await loadUsersOrgsData({ fresh: true });
    setOrgDeleteConfirm(null);
    setOrgDeletePasswordConfirm('');
    setOrgDeleteAuthError('');
    setOrgDeleteVerifying(false);
  }, [
    orgDeleteConfirm,
    orgDeletePasswordConfirm,
    canDeleteOrganizations,
    orgDetailOpen,
    orgDetailEditingId,
    closeOrgDetail,
    userDetailOpen,
    userDetailKey,
    closeUserDetail,
    loadUsersOrgsData,
  ]);

  const handleUserDetailSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!userDetailKey) return;
      const editingRow = allUsersRows.find((row) => usersOrgsUserRowKey(row) === userDetailKey);
      if (editingRow?.isApiUser) {
        const userId = String(editingRow.userId || editingRow.id || '').trim();
        if (!userId) return;
        try {
          setUsersOrgsError('');
          await updateUserAdmin({
            uu_id: userId,
            id: userId,
            name: userDetailForm.name.trim(),
            role: userDetailForm.role,
          });
          await loadUsersOrgsData({ fresh: true });
          closeUserDetail();
        } catch (err) {
          setUsersOrgsError(
            toUserFacingErrorMessage(err?.message, 'Failed to update user.')
          );
        }
        return;
      }
      const selectedOrg = organizationRows.find((o) => o.id === userDetailForm.organizationId);
      setUserOverrides((prev) => ({
        ...prev,
        [userDetailKey]: {
          name: userDetailForm.name.trim(),
          email: userDetailForm.email.trim(),
          password: userDetailForm.password,
          role: normalizeUserRoleForForm(userDetailForm.role),
          ...(userDetailForm.organizationId
            ? {
                orgId: userDetailForm.organizationId,
                organizationName:
                  selectedOrg?.name ?? userDetailForm.organization.trim(),
              }
            : {}),
        },
      }));
      closeUserDetail();
    },
    [userDetailKey, userDetailForm, organizationRows, closeUserDetail, allUsersRows, loadUsersOrgsData]
  );

  const requestDeleteUser = useCallback((row) => {
    setUserDeleteConfirm({ key: usersOrgsUserRowKey(row), name: row.name, row });
  }, []);

  const closeDeleteUserModal = useCallback(() => {
    setUserDeleteConfirm(null);
  }, []);

  const confirmDeleteUser = useCallback(async () => {
    if (!userDeleteConfirm) return;
    const { key, row } = userDeleteConfirm;
    if (row?.isApiUser) {
      const userId = String(row?.userId || row?.id || '').trim();
      if (!userId) return;
      try {
        setUsersOrgsError('');
        await deleteUserAdmin({ uu_id: userId, id: userId });
        await loadUsersOrgsData({ fresh: true });
      } catch (err) {
        setUsersOrgsError(
          toUserFacingErrorMessage(err?.message, 'Failed to delete user.')
        );
        return;
      }
    }
    if (userDetailOpen && userDetailKey === key) {
      closeUserDetail();
    }
    setDeletedUserKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setUserOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSelectedUserKeys((prev) => prev.filter((k) => k !== key));
    setUserDeleteConfirm(null);
  }, [userDeleteConfirm, userDetailOpen, userDetailKey, closeUserDetail, loadUsersOrgsData]);

  const closeBulkUserDeleteModal = useCallback(() => {
    setBulkUserDeleteKeys(null);
  }, []);

  const closeBulkOrgDeleteModal = useCallback(() => {
    setBulkOrgDeleteIds(null);
    setBulkOrgDeletePasswordConfirm('');
    setBulkOrgDeleteAuthError('');
    setBulkOrgDeleteVerifying(false);
  }, []);

  const requestBulkDeleteUsers = useCallback(() => {
    if (selectedUserKeys.length === 0) return;
    setBulkUserDeleteKeys([...selectedUserKeys]);
  }, [selectedUserKeys]);

  const requestBulkDeleteOrgs = useCallback(() => {
    if (!canDeleteOrganizations) {
      setUsersOrgsError('Only administrators can delete organizations.');
      return;
    }
    if (selectedOrgIds.length === 0) return;
    setBulkOrgDeleteAuthError('');
    setBulkOrgDeleteIds([...selectedOrgIds]);
  }, [selectedOrgIds, canDeleteOrganizations]);

  const confirmBulkDeleteUsers = useCallback(async () => {
    if (!bulkUserDeleteKeys?.length) return;
    const keys = bulkUserDeleteKeys;
    const rowByKey = new Map(allUsersRows.map((row) => [usersOrgsUserRowKey(row), row]));
    const apiRows = keys
      .map((key) => rowByKey.get(key))
      .filter((row) => row?.isApiUser);

    if (apiRows.length > 0) {
      setUsersOrgsError('');
      const results = await Promise.allSettled(
        apiRows.map((row) => {
          const userId = String(row?.userId || row?.id || '').trim();
          return deleteUserAdmin({ uu_id: userId, id: userId });
        })
      );
      if (results.some((r) => r.status === 'rejected')) {
        setUsersOrgsError('Failed to delete one or more selected users.');
      }
      await loadUsersOrgsData({ fresh: true });
    }

    const localKeys = keys.filter((key) => {
      const row = rowByKey.get(key);
      return !row?.isApiUser;
    });

    if (localKeys.length > 0) {
      if (userDetailOpen && userDetailKey && localKeys.includes(userDetailKey)) {
        closeUserDetail();
      }
      setDeletedUserKeys((prev) => [...new Set([...prev, ...localKeys])]);
      setUserOverrides((prev) => {
        const next = { ...prev };
        localKeys.forEach((k) => delete next[k]);
        return next;
      });
    }

    if (userDetailOpen && userDetailKey && keys.includes(userDetailKey)) {
      closeUserDetail();
    }
    setBulkUserDeleteKeys(null);
    setSelectedUserKeys([]);
  }, [bulkUserDeleteKeys, userDetailOpen, userDetailKey, closeUserDetail, allUsersRows, loadUsersOrgsData]);

  const confirmBulkDeleteOrganizations = useCallback(async () => {
    if (!bulkOrgDeleteIds?.length) return;
    if (!canDeleteOrganizations) return;
    if (bulkOrgDeletePasswordConfirm.trim().length < BULK_ORG_DELETE_PASSWORD_MIN_LEN) return;

    setBulkOrgDeleteVerifying(true);
    setBulkOrgDeleteAuthError('');
    try {
      await verifyProfileCurrentPassword(bulkOrgDeletePasswordConfirm);
    } catch (err) {
      setBulkOrgDeleteAuthError(
        toUserFacingErrorMessage(err?.message, 'Password verification failed.')
      );
      setBulkOrgDeleteVerifying(false);
      return;
    }

    const deleteResults = await Promise.allSettled(
      bulkOrgDeleteIds.map((id) => deleteOrganization({ uu_id: id }))
    );
    const deletedIds = bulkOrgDeleteIds.filter((_, idx) => deleteResults[idx]?.status === 'fulfilled');
    const failedIds = bulkOrgDeleteIds.filter((_, idx) => deleteResults[idx]?.status !== 'fulfilled');

    if (deletedIds.length === 0) {
      const firstErr = deleteResults.find((r) => r.status === 'rejected');
      setBulkOrgDeleteAuthError(
        toSafeOrganizationActionError(firstErr?.reason, 'Failed to delete selected organizations.')
      );
      setBulkOrgDeleteVerifying(false);
      return;
    }

    if (failedIds.length > 0) {
      setUsersOrgsError(`Deleted ${deletedIds.length} organizations. ${failedIds.length} deletions failed.`);
    } else {
      setUsersOrgsError('');
    }

    const idSet = new Set(deletedIds);
    if (orgDetailOpen && orgDetailEditingId && idSet.has(orgDetailEditingId)) {
      closeOrgDetail();
    }
    if (userDetailOpen && userDetailKey) {
      const oid = userDetailKey.split(':')[0];
      if (idSet.has(oid)) closeUserDetail();
    }
    setUserOverrides((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (idSet.has(k.split(':')[0])) delete next[k];
      }
      return next;
    });
    setDeletedUserKeys((prev) => prev.filter((k) => !idSet.has(k.split(':')[0])));
    setAdHocUserRows((rows) => rows.filter((r) => !idSet.has(r.orgId)));
    setOrganizationRows((rows) => rows.filter((r) => !idSet.has(r.id)));
    setOrganizationSiteRows((rows) =>
      rows.filter((s) => {
        const oid = String(s.orgId || '').trim();
        return !oid || !idSet.has(oid);
      })
    );
    await loadUsersOrgsData({ fresh: true });
    setBulkOrgDeleteIds(null);
    setBulkOrgDeletePasswordConfirm('');
    setBulkOrgDeleteAuthError('');
    setBulkOrgDeleteVerifying(false);
    setSelectedOrgIds([]);
  }, [
    bulkOrgDeleteIds,
    bulkOrgDeletePasswordConfirm,
    canDeleteOrganizations,
    orgDetailOpen,
    orgDetailEditingId,
    userDetailOpen,
    userDetailKey,
    closeOrgDetail,
    closeUserDetail,
    loadUsersOrgsData,
  ]);

  useEffect(() => {
    if (
      !addInviteUserModalOpen &&
      !addOrganizationModalOpen &&
      !bulkUserDeleteKeys &&
      !bulkOrgDeleteIds &&
      !userDeleteConfirm &&
      !userDetailOpen &&
      !orgDeleteConfirm &&
      !orgDetailOpen
    ) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (addInviteUserModalOpen) {
        closeAddInviteUserModal();
      } else if (addOrganizationModalOpen) {
        closeAddOrganizationModal();
      } else if (bulkUserDeleteKeys) {
        closeBulkUserDeleteModal();
      } else if (bulkOrgDeleteIds) {
        closeBulkOrgDeleteModal();
      } else if (userDeleteConfirm) {
        closeDeleteUserModal();
      } else if (userDetailOpen) {
        closeUserDetail();
      } else if (orgDeleteConfirm) {
        closeDeleteOrganizationModal();
      } else {
        closeOrgDetail();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [
    addInviteUserModalOpen,
    addOrganizationModalOpen,
    bulkUserDeleteKeys,
    bulkOrgDeleteIds,
    userDeleteConfirm,
    userDetailOpen,
    orgDeleteConfirm,
    orgDetailOpen,
    closeAddInviteUserModal,
    closeAddOrganizationModal,
    closeBulkUserDeleteModal,
    closeBulkOrgDeleteModal,
    closeDeleteUserModal,
    closeUserDetail,
    closeDeleteOrganizationModal,
    closeOrgDetail,
  ]);

  if (usersOrgsLoading) {
    return (
      <section className="assets-section users-orgs-settings-page" aria-labelledby="users-orgs-settings-heading">
        <PageDataLoader label={isUsersTab ? 'Loading users' : 'Loading organizations'} />
      </section>
    );
  }

  return (
    <section className="assets-section users-orgs-settings-page" aria-labelledby="users-orgs-settings-heading">
      <h2 id="users-orgs-settings-heading" className="edi-sr-only">
        Manage Organizations
      </h2>
      <div
        id="users-orgs-tab-panel"
        role="tabpanel"
        aria-labelledby="users-orgs-settings-heading"
        className="users-orgs-settings-panel"
      >
        <div className="scans-network-split-table-stack users-orgs-settings-search-table-block">
          <div className="manage-users-filters-row partners-settings-filters-row">
            <div className="risks-page-filters">
              {isUsersTab && selectedUserKeys.length > 0 ? (
                <button
                  type="button"
                  className="users-orgs-users-action-btn users-orgs-users-action-btn--danger users-orgs-users-action-btn--compact"
                  onClick={requestBulkDeleteUsers}
                  title={
                    `Delete ${selectedUserKeys.length} selected user${selectedUserKeys.length === 1 ? '' : 's'}`
                  }
                >
                  Delete Selected ({selectedUserKeys.length})
                </button>
              ) : null}
              {!isUsersTab && orgsHeaderActionsHost
                ? createPortal(
                    <button
                      type="button"
                      data-tour="settings-orgs-add"
                      className="users-orgs-users-action-btn users-orgs-users-action-btn--primary users-orgs-users-action-btn--compact users-orgs-top-header-manage-btn page-header-cta"
                      onClick={handleAddOrganization}
                      disabled={!canCreateOrganizations}
                      title={!canCreateOrganizations ? 'You do not have permission to create organizations.' : undefined}
                    >
                      <svg className="page-header-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      Add Organization
                    </button>,
                    orgsHeaderActionsHost
                  )
                : null}
            {!isUsersTab && organizationsSiteFilterSupported ? (
              <div className="users-orgs-users-company-filter users-orgs-site-filter-field" ref={organizationsSiteFilterRef}>
                <div className="users-orgs-users-company-filter-bar">
                  <button
                    type="button"
                    className={`users-orgs-users-company-filter-main${
                      !organizationsSiteFilterId ? ' users-orgs-users-company-filter-main--all-orgs' : ''
                    }`}
                    aria-expanded={organizationsSiteFilterOpen}
                    aria-haspopup="listbox"
                    title="Filter by site"
                    aria-label={
                      organizationsSiteFilterId
                        ? `Choose site filter. ${organizationsSiteFilterActiveName || 'Site'} is selected.`
                        : 'Filter by site. All sites shown.'
                    }
                    onClick={() =>
                      setOrganizationsSiteFilterOpen((open) => {
                        const next = !open;
                        if (!next) setOrganizationsSiteFilterSearch('');
                        return next;
                      })
                    }
                  >
                    <svg
                      className="users-orgs-users-company-filter-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span
                      className={`users-orgs-users-company-filter-label${
                        organizationsSiteFilterId ? ' users-orgs-users-company-filter-label--active' : ''
                      }`}
                      title={organizationsSiteFilterId ? organizationsSiteFilterActiveName || undefined : undefined}
                    >
                      {organizationsSiteFilterId ? organizationsSiteFilterActiveName || 'Site' : 'All Sites'}
                    </span>
                    <span className="users-orgs-users-company-filter-chevron" aria-hidden="true"></span>
                  </button>
                  {organizationsSiteFilterId ? (
                    <button
                      type="button"
                      className="users-orgs-users-company-filter-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOrganizationsSiteFilterId('');
                        setOrganizationsSiteFilterOpen(false);
                        setOrganizationsSiteFilterSearch('');
                      }}
                      aria-label="Clear site filter"
                      title="Clear site filter"
                    >
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                {organizationsSiteFilterOpen ? (
                  <div className="users-orgs-users-company-filter-panel" role="presentation">
                    <div className="users-orgs-users-company-filter-search-wrap">
                      <label htmlFor={`${usersOrgsSearchInputId}-site-filter-search`} className="edi-sr-only">
                        Search sites for filter
                      </label>
                      <svg
                        width={13}
                        height={13}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="users-orgs-users-company-filter-search-icon"
                        aria-hidden="true"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        id={`${usersOrgsSearchInputId}-site-filter-search`}
                        type="text"
                        className="users-orgs-users-company-filter-search"
                        placeholder="Search sites..."
                        value={organizationsSiteFilterSearch}
                        onChange={(e) => setOrganizationsSiteFilterSearch(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        autoComplete="off"
                      />
                    </div>
                    <ul className="users-orgs-users-company-filter-list" role="listbox">
                      <li role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={!organizationsSiteFilterId}
                          className={`users-orgs-users-company-filter-option${
                            !organizationsSiteFilterId ? ' is-selected' : ''
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setOrganizationsSiteFilterId('');
                            setOrganizationsSiteFilterOpen(false);
                            setOrganizationsSiteFilterSearch('');
                          }}
                        >
                          All Sites
                        </button>
                      </li>
                      {organizationsSiteFilterList.map((site) => (
                        <li key={site.id} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={String(site.id).trim() === String(organizationsSiteFilterId).trim()}
                            className={`users-orgs-users-company-filter-option${
                              String(site.id).trim() === String(organizationsSiteFilterId).trim() ? ' is-selected' : ''
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setOrganizationsSiteFilterId(String(site.id).trim());
                              setOrganizationsSiteFilterOpen(false);
                              setOrganizationsSiteFilterSearch('');
                            }}
                          >
                            {site.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
            {!isUsersTab && canDeleteOrganizations && selectedOrgIds.length > 0 ? (
              <button
                type="button"
                className="users-orgs-users-action-btn users-orgs-users-action-btn--danger users-orgs-users-action-btn--compact users-orgs-org-delete-btn"
                onClick={requestBulkDeleteOrgs}
              >
                Delete ({selectedOrgIds.length})
              </button>
            ) : null}
            <div className="subscription-org-management-search manage-users-inline-search" data-tour="settings-orgs-search">
              <label htmlFor={usersOrgsSearchInputId} className="edi-sr-only">
                {searchPlaceholder}
              </label>
              <div className="subscription-org-management-search-inner">
                <input
                  id={usersOrgsSearchInputId}
                  type="search"
                  className="subscription-org-management-search-input"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(sanitizeUniversalSearchInput(e.target.value))}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={UNIVERSAL_SEARCH_MAX_LEN}
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="ghost-exposure-sys-table-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>
            </div>
          </div>
          {usersOrgsError ? (
            <div className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 10 }}>
              <span>{usersOrgsError}</span>
              {' '}
              <button
                type="button"
                className="ghost-exposure-sys-btn"
                style={{ marginLeft: 8 }}
                onClick={() => loadUsersOrgsData({ fresh: true, includeUsers: isUsersTab })}
              >
                Retry
              </button>
            </div>
          ) : null}
          <div className="assets-table-shell users-orgs-settings-table-shell assets-table-shell--inventory-body" data-tour="settings-orgs-table">
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${usersOrgsHeaderGutterPx}px` }}
            >
              <div ref={usersOrgsTableHeaderRef} className="risks-page-table-header">
                <table className={`${usersOrgsTableClassName} risks-page-table--header`} style={usersOrgsTableStyle}>
                  <ResizableColgroup columns={usersOrgsTableColumns} widths={usersOrgsColumnWidths} />
                  <ResizableTableHead
                    columns={usersOrgsTableColumns}
                    widths={usersOrgsColumnWidths}
                    onResizeStart={onUsersOrgsColumnResizeStart}
                    resizingKey={usersOrgsColumnResizingKey}
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                    getSortIcon={sortIcon}
                    resizeHandleClassName="risks-page-col-resize-handle"
                  />
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={usersOrgsTableScrollRef}
              className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
              onScroll={handleUsersOrgsTableScroll}
            >
              <table className={`${usersOrgsTableClassName} risks-page-table--body`} style={usersOrgsTableStyle}>
                <ResizableColgroup columns={usersOrgsTableColumns} widths={usersOrgsColumnWidths} />
                <tbody>
                {usersOrgsLoading ? (
                  <tr>
                    <td colSpan={isUsersTab ? 7 : 6} className="assets-empty-cell">
                      <BlueXLoader size="small" label="Loading users and organizations" />
                    </td>
                  </tr>
                ) : sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={isUsersTab ? 7 : 6} className="assets-empty-cell">
                      {emptyMessage}
                    </td>
                  </tr>
                ) : isUsersTab ? (
                  visibleRows.map((row) => (
                    <tr key={usersOrgsUserRowKey(row)}>
                      <td className="users-orgs-settings-td-checkbox">
                        <input
                          type="checkbox"
                          className="users-orgs-settings-checkbox"
                          checked={selectedUserKeys.includes(usersOrgsUserRowKey(row))}
                          onChange={() => toggleUserRowSelected(row)}
                          aria-label={`Select user ${row.name}`}
                        />
                      </td>
                      <td>
                        <span className="users-orgs-user-name">{row.name}</span>
                      </td>
                      <td>
                        <span className="users-orgs-user-org-cell">{row.organizationName}</span>
                      </td>
                      <td>{row.perimeterHosts}</td>
                      <td>{row.onPremHosts}</td>
                      <td>{formatUsersOrgsTableDate(row.createdAt)}</td>
                      <td className="users-orgs-settings-td-actions">
                        <div className="users-orgs-actions-cell users-orgs-settings-actions-cell">
                          <button
                            type="button"
                            className="users-orgs-action-view-btn"
                            onClick={() => openUserDetail(row)}
                            aria-label={`View user ${row.name}`}
                            title="View user"
                          >
                            <svg
                              className="users-orgs-action-view-icon"
                              width={16}
                              height={16}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="connectors-settings-row-action connectors-settings-row-action--danger connectors-settings-row-action--icon-only"
                            title={`Delete ${row.name}`}
                            aria-label={`Delete user ${row.name}`}
                            onClick={() => requestDeleteUser(row)}
                          >
                            <TrashIcon className="connectors-settings-delete-icon" size={14} strokeWidth={2.35} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  visibleRows.map((row) => (
                    <tr key={row.id}>
                      <td className="users-orgs-settings-td-checkbox">
                        <input
                          type="checkbox"
                          className="users-orgs-settings-checkbox"
                          checked={selectedOrgIds.includes(row.id)}
                          disabled={!canDeleteOrganizations}
                          onChange={() => toggleOrgRowSelected(row)}
                          aria-label={`Select organization ${row.name}`}
                          title={!canDeleteOrganizations ? 'Only administrators can delete organizations.' : undefined}
                        />
                      </td>
                      <td>
                        <span className="users-orgs-org-name-cell">{row.name}</span>
                      </td>
                      <td>{row.perimeterHosts}</td>
                      <td>{row.onPremHosts}</td>
                      <td>{formatUsersOrgsTableDate(row.createdAt)}</td>
                      <td className="users-orgs-settings-td-actions">
                        <div className="users-orgs-actions-cell users-orgs-settings-actions-cell">
                          {canDeleteOrganizations ? (
                            <button
                              type="button"
                              className="connectors-settings-row-action connectors-settings-row-action--danger connectors-settings-row-action--icon-only"
                              title={`Delete ${row.name}`}
                              aria-label={`Delete organization ${row.name}`}
                              onClick={() => {
                                closeOrgRowActionsMenu();
                                requestDeleteOrganization(row);
                              }}
                            >
                              <TrashIcon className="connectors-settings-delete-icon" size={14} strokeWidth={2.35} />
                            </button>
                          ) : null}
                          <div
                            className="assets-row-actions-menu-wrap"
                            ref={openOrgRowMenuKey === String(row.id) ? orgRowActionsTriggerRef : null}
                          >
                            <button
                              type="button"
                              className={[
                                'assets-more-btn',
                                openOrgRowMenuKey === String(row.id) ? 'assets-more-btn--open' : null,
                              ].filter(Boolean).join(' ')}
                              title="More actions"
                              aria-label={`More actions for organization ${row.name}`}
                              aria-haspopup="menu"
                              aria-expanded={openOrgRowMenuKey === String(row.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                const menuKey = String(row.id);
                                if (openOrgRowMenuKey === menuKey) {
                                  closeOrgRowActionsMenu();
                                  return;
                                }
                                const trigger = e.currentTarget;
                                if (!trigger) return;
                                const rect = trigger.getBoundingClientRect();
                                const estimatedMenuHeight = 52;
                                const openUp = window.innerHeight - rect.bottom < estimatedMenuHeight + 16;
                                const isLightTheme = Boolean(
                                  document.querySelector('.dashboard-container.theme-light')
                                );
                                setOrgRowMenuPlacement({
                                  top: openUp ? rect.top - 4 : rect.bottom + 4,
                                  right: Math.max(8, window.innerWidth - rect.right),
                                  openUp,
                                  isLightTheme,
                                });
                                setOpenOrgRowMenuKey(menuKey);
                              }}
                            >
                              <svg
                                className="assets-more-btn-icon"
                                viewBox="0 0 20 20"
                                width="14"
                                height="14"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <circle cx="4" cy="10" r="1.6" />
                                <circle cx="10" cy="10" r="1.6" />
                                <circle cx="16" cy="10" r="1.6" />
                              </svg>
                            </button>
                            {openOrgRowMenuKey === String(row.id) && orgRowMenuPlacement
                              ? createPortal(
                                  <div
                                    ref={orgRowActionsMenuRef}
                                    className={[
                                      'assets-row-actions-menu',
                                      'assets-row-actions-menu--portal',
                                      orgRowMenuPlacement.openUp ? 'assets-row-actions-menu--up' : null,
                                      orgRowMenuPlacement.isLightTheme
                                        ? 'assets-row-actions-menu--light'
                                        : null,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    role="menu"
                                    style={{
                                      top: orgRowMenuPlacement.top,
                                      right: orgRowMenuPlacement.right,
                                    }}
                                  >
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="assets-row-actions-menu-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        closeOrgRowActionsMenu();
                                        openOrgDetail(row);
                                      }}
                                    >
                                      Edit
                                    </button>
                                  </div>,
                                  document.body
                                )
                              : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          </div>
          <TablePagination {...tablePagination} itemLabel={isUsersTab ? 'users' : 'organizations'} />
        </div>
      </div>

      {orgDetailOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeOrgDetail();
          }}
        >
          <div
            className="assets-detail-modal users-orgs-org-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-orgs-org-detail-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id="users-orgs-org-detail-title">
                  Organization Details
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeOrgDetail}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <form className="users-orgs-org-detail-form" onSubmit={handleOrgDetailSubmit}>
                <div className="users-orgs-org-detail-form-scroll">
                  <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-org-details-heading">
                    <div className="users-orgs-add-invite-card-head">
                      <h3 id="users-orgs-org-details-heading" className="users-orgs-add-invite-card-title">
                        Organization details
                      </h3>
                      <p className="users-orgs-org-assign-hint">Name, contact, and location information.</p>
                    </div>
                    <div className="users-orgs-add-invite-card-body">
                      <div className="users-orgs-org-detail-field">
                        <label htmlFor={orgDetailNameId}>Name</label>
                        <input
                          id={orgDetailNameId}
                          type="text"
                          className="users-orgs-org-detail-input"
                          value={orgDetailForm.name}
                          onChange={(e) => setOrgDetailForm((f) => ({ ...f, name: e.target.value }))}
                          autoComplete="organization"
                        />
                      </div>
                      <div className="users-orgs-org-detail-field">
                        <label htmlFor={orgDetailDescId}>Description</label>
                        <input
                          id={orgDetailDescId}
                          type="text"
                          className="users-orgs-org-detail-input"
                          value={orgDetailForm.description}
                          onChange={(e) => setOrgDetailForm((f) => ({ ...f, description: e.target.value }))}
                        />
                      </div>
                      <div className="users-orgs-org-detail-field">
                        <label htmlFor={orgDetailContactId}>Contact</label>
                        <input
                          id={orgDetailContactId}
                          type="text"
                          className="users-orgs-org-detail-input"
                          value={orgDetailForm.contact}
                          onChange={(e) => setOrgDetailForm((f) => ({ ...f, contact: e.target.value }))}
                          autoComplete="tel"
                        />
                      </div>
                      <div className="users-orgs-org-detail-field">
                        <label htmlFor={orgDetailAddressId}>Address</label>
                        <input
                          id={orgDetailAddressId}
                          type="text"
                          className="users-orgs-org-detail-input"
                          value={orgDetailForm.address}
                          onChange={(e) => setOrgDetailForm((f) => ({ ...f, address: e.target.value }))}
                          autoComplete="street-address"
                        />
                      </div>
                      {canEditOrgPlatformFields ? (
                        <div className="users-orgs-org-detail-field">
                          <label htmlFor={orgDetailManagedById}>Managed by</label>
                          <select
                            id={orgDetailManagedById}
                            className="users-orgs-org-detail-select"
                            value={orgDetailForm.managedBy}
                            onChange={(e) => setOrgDetailForm((f) => ({ ...f, managedBy: e.target.value }))}
                          >
                            <option value="">Not managed by another organization</option>
                            {orgDetailManagedByOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                    </div>
                  </section>

                  {canEditOrgPlatformFields ? (
                    <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-org-options-heading">
                      <div className="users-orgs-add-invite-card-head">
                        <h3 id="users-orgs-org-options-heading" className="users-orgs-add-invite-card-title">
                          Options
                        </h3>
                        <p className="users-orgs-org-assign-hint">Default scan and site settings for this organization.</p>
                      </div>
                      <div className="users-orgs-add-invite-card-body">
                        <div className="users-orgs-org-detail-check-row">
                          <input
                            id={orgDetailCbEcommerceId}
                            className="users-orgs-org-detail-check-input"
                            type="checkbox"
                            checked={Boolean(orgDetailForm.ecommerceMode)}
                            onChange={(e) =>
                              setOrgDetailForm((f) => ({ ...f, ecommerceMode: e.target.checked }))
                            }
                          />
                          <label className="users-orgs-org-detail-check-label" htmlFor={orgDetailCbEcommerceId}>
                            eCommerce Mode
                          </label>
                        </div>
                        <div className="users-orgs-org-detail-check-row">
                          <input
                            id={orgDetailCbWebScanId}
                            className="users-orgs-org-detail-check-input"
                            type="checkbox"
                            checked={Boolean(orgDetailForm.webScanEnabled)}
                            onChange={(e) =>
                              setOrgDetailForm((f) => ({ ...f, webScanEnabled: e.target.checked }))
                            }
                          />
                          <label className="users-orgs-org-detail-check-label" htmlFor={orgDetailCbWebScanId}>
                            Web Application Scan Enabled
                          </label>
                        </div>
                      </div>
                    </section>
                  ) : null}
                </div>

                <div className="users-orgs-org-detail-actions">
                  <button type="submit" className="users-orgs-org-detail-save-btn">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {orgDeleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDeleteOrganizationModal();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete organization"
            aria-describedby={`users-orgs-org-delete-desc ${orgDeletePasswordHintId}`}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeDeleteOrganizationModal}
              aria-label="Close"
            >
              ✕
            </button>
            <p id="users-orgs-org-delete-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete organization{' '}
              <strong className="assets-exclude-confirm-name">{orgDeleteConfirm.name}</strong>.
              Related users and sites will be removed from the list.
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="users-orgs-delete-confirm-password-block">
              <label htmlFor={orgDeletePasswordConfirmId} className="users-orgs-delete-confirm-password-label">
                Enter your account password to confirm
              </label>
              <input
                id={orgDeletePasswordConfirmId}
                type="password"
                className="users-orgs-delete-confirm-password-input"
                autoComplete="current-password"
                value={orgDeletePasswordConfirm}
                onChange={(e) => {
                  setOrgDeletePasswordConfirm(e.target.value);
                  setOrgDeleteAuthError('');
                }}
                aria-describedby={orgDeletePasswordHintId}
              />
              <span id={orgDeletePasswordHintId} className="edi-sr-only">
                Password must be at least {BULK_ORG_DELETE_PASSWORD_MIN_LEN} characters to enable Delete.
              </span>
            </div>
            {orgDeleteAuthError ? (
              <p className="users-orgs-delete-confirm-auth-error" role="alert">
                {orgDeleteAuthError}
              </p>
            ) : null}
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteOrganization}
                disabled={
                  orgDeleteVerifying ||
                  orgDeletePasswordConfirm.trim().length < BULK_ORG_DELETE_PASSWORD_MIN_LEN
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {userDetailOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeUserDetail();
          }}
        >
          <div
            className="assets-detail-modal users-orgs-user-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-orgs-user-detail-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id="users-orgs-user-detail-title">
                  User Details
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeUserDetail}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <form className="users-orgs-user-detail-form" onSubmit={handleUserDetailSubmit}>
                <div className="users-orgs-user-detail-form-scroll">
                <div className="users-orgs-user-detail-field">
                  <label htmlFor={userDetailNameId}>Name</label>
                  <input
                    id={userDetailNameId}
                    type="text"
                    className="users-orgs-user-detail-input"
                    value={userDetailForm.name}
                    onChange={(e) => setUserDetailForm((f) => ({ ...f, name: e.target.value }))}
                    autoComplete="name"
                  />
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor={userDetailEmailId}>Email</label>
                  <input
                    id={userDetailEmailId}
                    type="email"
                    className="users-orgs-user-detail-input"
                    value={userDetailForm.email}
                    onChange={(e) => setUserDetailForm((f) => ({ ...f, email: e.target.value }))}
                    autoComplete="email"
                  />
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor={userDetailPasswordId}>Password</label>
                  <input
                    id={userDetailPasswordId}
                    type="password"
                    readOnly
                    className="users-orgs-user-detail-input users-orgs-user-detail-password-masked"
                    value=""
                    aria-readonly="true"
                    aria-label="Password. The current password is not shown and cannot be edited here."
                    aria-describedby={userDetailPasswordHintId}
                    autoComplete="off"
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                  />
                  <p id={userDetailPasswordHintId} className="users-orgs-user-detail-password-hint">
                    The current password cannot be viewed here.
                  </p>
                  <button
                    type="button"
                    className="users-orgs-user-detail-reset-pw-link"
                    onClick={handleUserPasswordResetRequest}
                  >
                    Reset password
                  </button>
                  {userPasswordResetStatus ? (
                    <p className="users-orgs-user-detail-reset-pw-status" role="status">
                      {userPasswordResetStatus}
                    </p>
                  ) : null}
                </div>
                <div className="users-orgs-user-detail-field">
                  <div className="users-orgs-user-detail-label-row">
                    <span id={userDetailOrgId} className="users-orgs-user-detail-field-heading">
                      Organization
                    </span>
                  </div>
                  <div className="users-orgs-org-combobox" ref={userDetailOrgPickerRef}>
                    <button
                      type="button"
                      className="users-orgs-org-combobox-trigger users-orgs-user-detail-input"
                      aria-expanded={userDetailOrgPickerOpen}
                      aria-haspopup="listbox"
                      aria-labelledby={userDetailOrgId}
                      onClick={() => {
                        setUserDetailOrgPickerOpen((open) => {
                          const next = !open;
                          if (!next) setUserDetailOrgSearch('');
                          return next;
                        });
                      }}
                    >
                      <span className="users-orgs-org-combobox-trigger-text">
                        {userDetailForm.organization || 'Select organization'}
                      </span>
                      <span className="users-orgs-org-combobox-caret" aria-hidden>
                        
                      </span>
                    </button>
                    {userDetailOrgPickerOpen ? (
                      <div className="users-orgs-org-combobox-panel">
                        <input
                          id={userDetailOrgSearchInputId}
                          type="search"
                          className="users-orgs-org-combobox-search users-orgs-user-detail-input"
                          placeholder="Search organizations"
                          value={userDetailOrgSearch}
                          onChange={(e) => setUserDetailOrgSearch(e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                        <ul className="users-orgs-org-combobox-list" role="listbox">
                          {userDetailOrgPickerFiltered.length === 0 ? (
                            <li className="users-orgs-org-combobox-empty">No matches</li>
                          ) : (
                            userDetailOrgPickerFiltered.map((org) => (
                              <li key={org.id} role="presentation">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={org.id === userDetailForm.organizationId}
                                  className={`users-orgs-org-combobox-option${
                                    org.id === userDetailForm.organizationId ? ' is-selected' : ''
                                  }`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setUserDetailForm((f) => ({
                                      ...f,
                                      organizationId: org.id,
                                      organization: org.name,
                                    }));
                                    setUserDetailOrgPickerOpen(false);
                                    setUserDetailOrgSearch('');
                                  }}
                                >
                                  {org.name}
                                </button>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor={userDetailRoleId}>Role</label>
                  <select
                    id={userDetailRoleId}
                    className="users-orgs-user-detail-input users-orgs-user-detail-select"
                    value={
                      USER_DETAIL_ROLE_OPTIONS.some((o) => o.value === userDetailForm.role)
                        ? userDetailForm.role
                        : 'Viewer'
                    }
                    onChange={(e) => setUserDetailForm((f) => ({ ...f, role: e.target.value }))}
                  >
                    {USER_DETAIL_ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                </div>
                <div className="users-orgs-user-detail-actions">
                  <button type="submit" className="users-orgs-user-detail-save-btn">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {userDeleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDeleteUserModal();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete user"
            aria-describedby="users-orgs-user-delete-desc"
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeDeleteUserModal}
              aria-label="Close"
            >
              ✕
            </button>
            <p id="users-orgs-user-delete-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete{' '}
              <strong className="assets-exclude-confirm-name">{userDeleteConfirm.name}</strong>.
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteUser}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {addInviteUserModalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAddInviteUserModal();
          }}
        >
          <div
            className={`assets-detail-modal users-orgs-user-detail-modal users-orgs-add-invite-user-modal${
              addInviteUserStep === 'choice' ? ' users-orgs-add-invite-user-modal--choice' : ''
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-orgs-add-invite-user-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header users-orgs-add-invite-modal-header">
                <div className="users-orgs-add-invite-header-left">
                  {addInviteUserStep !== 'choice' ? (
                    <button
                      type="button"
                      className="users-orgs-add-invite-step-back assets-detail-modal-close"
                      onClick={() => {
                        setAddInviteUserStep('choice');
                        setAddInviteUserModalError('');
                        setAddUserOrgSearch('');
                        setAddUserSiteSearch('');
                        setInviteUserOrgSearch('');
                        setInviteUserSiteSearch('');
                      }}
                      aria-label="Back"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <div className="assets-detail-modal-title users-orgs-add-invite-modal-title" id="users-orgs-add-invite-user-title">
                  {addInviteUserStep === 'choice'
                    ? 'Add or Invite User'
                    : addInviteUserStep === 'add'
                      ? 'Add a new user'
                      : 'Invite new user'}
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeAddInviteUserModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="users-orgs-add-invite-user-body">
                {addInviteUserStep === 'choice' ? (
                  <div className="new-scan-modal-options" role="group" aria-label="How to add a user">
                    <button
                      type="button"
                      className="new-scan-modal-option"
                      onClick={() => {
                        setAddInviteUserModalError('');
                        setAddInviteUserStep('add');
                      }}
                    >
                      <span className="new-scan-modal-option-label">Add user</span>
                      <span className="new-scan-modal-option-desc">Create the account now</span>
                    </button>
                    <button
                      type="button"
                      className="new-scan-modal-option"
                      onClick={() => {
                        setAddInviteUserModalError('');
                        setAddInviteUserStep('invite');
                      }}
                    >
                      <span className="new-scan-modal-option-label">Invite user</span>
                      <span className="new-scan-modal-option-desc">Send an email invitation</span>
                    </button>
                  </div>
                ) : addInviteUserStep === 'add' ? (
                  <form className="users-orgs-user-detail-form users-orgs-add-invite-form" onSubmit={submitAddUserModal}>
                    {addInviteUserModalError ? (
                      <div className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 12 }}>
                        {addInviteUserModalError}
                      </div>
                    ) : null}
                    <div className="users-orgs-add-invite-form-scroll">
                      <section className="users-orgs-add-invite-card" aria-label="User details">
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-name-split">
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor={addUserFormNameId}>First name</label>
                              <input
                                id={addUserFormNameId}
                                type="text"
                                className={`users-orgs-user-detail-input${addUserFieldErrors.firstName ? ' is-invalid' : ''}`}
                                placeholder="First name"
                                value={addUserModalForm.firstName}
                                onChange={(e) => {
                                  setAddUserFieldErrors((prev) =>
                                    prev.firstName ? { ...prev, firstName: '' } : prev
                                  );
                                  setAddUserModalForm((f) => ({ ...f, firstName: e.target.value }));
                                }}
                                autoComplete="given-name"
                                aria-invalid={Boolean(addUserFieldErrors.firstName)}
                              />
                              {addUserFieldErrors.firstName ? (
                                <p className="users-orgs-field-error" role="alert">
                                  {addUserFieldErrors.firstName}
                                </p>
                              ) : null}
                            </div>
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor={addUserFormLastNameId}>Last name</label>
                              <input
                                id={addUserFormLastNameId}
                                type="text"
                                className={`users-orgs-user-detail-input${addUserFieldErrors.lastName ? ' is-invalid' : ''}`}
                                placeholder="Last name"
                                value={addUserModalForm.lastName}
                                onChange={(e) => {
                                  setAddUserFieldErrors((prev) =>
                                    prev.lastName ? { ...prev, lastName: '' } : prev
                                  );
                                  setAddUserModalForm((f) => ({ ...f, lastName: e.target.value }));
                                }}
                                autoComplete="family-name"
                                aria-invalid={Boolean(addUserFieldErrors.lastName)}
                              />
                              {addUserFieldErrors.lastName ? (
                                <p className="users-orgs-field-error" role="alert">
                                  {addUserFieldErrors.lastName}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="users-orgs-user-detail-field">
                            <label htmlFor={addUserFormEmailId}>Email</label>
                            <input
                              id={addUserFormEmailId}
                              type="email"
                              className={`users-orgs-user-detail-input${addUserFieldErrors.email ? ' is-invalid' : ''}`}
                              placeholder="Email"
                              value={addUserModalForm.email}
                              onChange={(e) => {
                                setAddUserFieldErrors((prev) =>
                                  prev.email ? { ...prev, email: '' } : prev
                                );
                                setAddUserModalForm((f) => ({ ...f, email: e.target.value }));
                              }}
                              autoComplete="email"
                              aria-invalid={Boolean(addUserFieldErrors.email)}
                            />
                            {addUserFieldErrors.email ? (
                              <p className="users-orgs-field-error" role="alert">
                                {addUserFieldErrors.email}
                              </p>
                            ) : null}
                          </div>
                          <div className="users-orgs-user-detail-field">
                            <label htmlFor={addUserFormPasswordId}>Temporary password</label>
                            <input
                              id={addUserFormPasswordId}
                              type="password"
                              className={`users-orgs-user-detail-input${addUserFieldErrors.password ? ' is-invalid' : ''}`}
                              placeholder="Temporary password"
                              value={addUserModalForm.password}
                              onChange={(e) => {
                                setAddUserFieldErrors((prev) =>
                                  prev.password ? { ...prev, password: '' } : prev
                                );
                                setAddUserModalForm((f) => ({ ...f, password: e.target.value }));
                              }}
                              autoComplete="new-password"
                              aria-invalid={Boolean(addUserFieldErrors.password)}
                            />
                            {addUserFieldErrors.password ? (
                              <p className="users-orgs-field-error" role="alert">
                                {addUserFieldErrors.password}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </section>
                      <UserScopeAssignFields
                        organizations={organizationRows}
                        sites={organizationSiteRows}
                        allOrganizationsAccess={addUserModalForm.allOrganizationsAccess}
                        allSitesAccess={addUserModalForm.allSitesAccess}
                        organizationAccessUuids={addUserModalForm.organizationAccessUuids}
                        siteAccessUuids={addUserModalForm.siteAccessUuids}
                        orgSearch={addUserOrgSearch}
                        siteSearch={addUserSiteSearch}
                        onOrgSearchChange={setAddUserOrgSearch}
                        onSiteSearchChange={setAddUserSiteSearch}
                        orgSearchId={addUserFormOrgSearchId}
                        siteSearchId={addUserFormSiteSearchId}
                        onChange={(patch) => setAddUserModalForm((f) => ({ ...f, ...patch }))}
                      />
                      <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-add-role-heading">
                        <div className="users-orgs-add-invite-card-head">
                          <h3 id="users-orgs-add-role-heading" className="users-orgs-add-invite-card-title">
                            Role
                          </h3>
                          <p className="users-orgs-org-assign-hint">Choose the access level for this user.</p>
                        </div>
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-user-detail-field">
                            <select
                              id={addUserFormRoleId}
                              className="users-orgs-user-detail-input users-orgs-user-detail-select"
                              aria-labelledby="users-orgs-add-role-heading"
                              value={
                                USER_DETAIL_ROLE_OPTIONS.some((o) => o.value === addUserModalForm.role)
                                  ? addUserModalForm.role
                                  : 'Viewer'
                              }
                              onChange={(e) =>
                                setAddUserModalForm((f) => ({ ...f, role: e.target.value }))
                              }
                            >
                              {USER_DETAIL_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </section>
                    </div>
                    <div className="users-orgs-user-detail-actions">
                      <button
                        type="submit"
                        className="users-orgs-user-detail-save-btn"
                        disabled={
                          !(
                            String(addUserModalForm.firstName || '').trim() ||
                            String(addUserModalForm.lastName || '').trim() ||
                            String(addUserModalForm.email || '').trim() ||
                            String(addUserModalForm.password || '').trim()
                          )
                        }
                      >
                        Save
                      </button>
                    </div>
                  </form>
                ) : (
                  <form className="users-orgs-user-detail-form users-orgs-add-invite-form" onSubmit={submitInviteUserModal}>
                    {addInviteUserModalError ? (
                      <div className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 12 }}>
                        {addInviteUserModalError}
                      </div>
                    ) : null}
                    <div className="users-orgs-add-invite-form-scroll">
                      <section className="users-orgs-add-invite-card" aria-label="User details">
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-name-split">
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor={inviteUserFormNameId}>First name</label>
                              <input
                                id={inviteUserFormNameId}
                                type="text"
                                className={`users-orgs-user-detail-input${inviteUserFieldErrors.firstName ? ' is-invalid' : ''}`}
                                placeholder="First name"
                                value={inviteUserModalForm.firstName}
                                onChange={(e) => {
                                  setInviteUserFieldErrors((prev) =>
                                    prev.firstName ? { ...prev, firstName: '' } : prev
                                  );
                                  setInviteUserModalForm((f) => ({ ...f, firstName: e.target.value }));
                                }}
                                autoComplete="given-name"
                                aria-invalid={Boolean(inviteUserFieldErrors.firstName)}
                              />
                              {inviteUserFieldErrors.firstName ? (
                                <p className="users-orgs-field-error" role="alert">
                                  {inviteUserFieldErrors.firstName}
                                </p>
                              ) : null}
                            </div>
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor={inviteUserFormLastNameId}>Last name</label>
                              <input
                                id={inviteUserFormLastNameId}
                                type="text"
                                className={`users-orgs-user-detail-input${inviteUserFieldErrors.lastName ? ' is-invalid' : ''}`}
                                placeholder="Last name"
                                value={inviteUserModalForm.lastName}
                                onChange={(e) => {
                                  setInviteUserFieldErrors((prev) =>
                                    prev.lastName ? { ...prev, lastName: '' } : prev
                                  );
                                  setInviteUserModalForm((f) => ({ ...f, lastName: e.target.value }));
                                }}
                                autoComplete="family-name"
                                aria-invalid={Boolean(inviteUserFieldErrors.lastName)}
                              />
                              {inviteUserFieldErrors.lastName ? (
                                <p className="users-orgs-field-error" role="alert">
                                  {inviteUserFieldErrors.lastName}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="users-orgs-user-detail-field">
                            <label htmlFor={inviteUserFormEmailId}>Email</label>
                            <input
                              id={inviteUserFormEmailId}
                              type="email"
                              className={`users-orgs-user-detail-input${inviteUserFieldErrors.email ? ' is-invalid' : ''}`}
                              placeholder="Email"
                              value={inviteUserModalForm.email}
                              onChange={(e) => {
                                setInviteUserFieldErrors((prev) =>
                                  prev.email ? { ...prev, email: '' } : prev
                                );
                                setInviteUserModalForm((f) => ({ ...f, email: e.target.value }));
                              }}
                              autoComplete="email"
                              aria-invalid={Boolean(inviteUserFieldErrors.email)}
                            />
                            {inviteUserFieldErrors.email ? (
                              <p className="users-orgs-field-error" role="alert">
                                {inviteUserFieldErrors.email}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </section>
                      <UserScopeAssignFields
                        organizations={organizationRows}
                        sites={organizationSiteRows}
                        allOrganizationsAccess={inviteUserModalForm.allOrganizationsAccess}
                        allSitesAccess={inviteUserModalForm.allSitesAccess}
                        organizationAccessUuids={inviteUserModalForm.organizationAccessUuids}
                        siteAccessUuids={inviteUserModalForm.siteAccessUuids}
                        orgSearch={inviteUserOrgSearch}
                        siteSearch={inviteUserSiteSearch}
                        onOrgSearchChange={setInviteUserOrgSearch}
                        onSiteSearchChange={setInviteUserSiteSearch}
                        orgSearchId={inviteUserFormOrgSearchId}
                        siteSearchId={inviteUserFormSiteSearchId}
                        onChange={(patch) => setInviteUserModalForm((f) => ({ ...f, ...patch }))}
                      />
                      <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-invite-role-heading">
                        <div className="users-orgs-add-invite-card-head">
                          <h3 id="users-orgs-invite-role-heading" className="users-orgs-add-invite-card-title">
                            Role
                          </h3>
                          <p className="users-orgs-org-assign-hint">Choose the access level for this user.</p>
                        </div>
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-user-detail-field">
                            <select
                              id={inviteUserFormRoleId}
                              className="users-orgs-user-detail-input users-orgs-user-detail-select"
                              aria-labelledby="users-orgs-invite-role-heading"
                              value={
                                USER_DETAIL_ROLE_OPTIONS.some((o) => o.value === inviteUserModalForm.role)
                                  ? inviteUserModalForm.role
                                  : 'Viewer'
                              }
                              onChange={(e) =>
                                setInviteUserModalForm((f) => ({ ...f, role: e.target.value }))
                              }
                            >
                              {USER_DETAIL_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </section>
                    </div>
                    <div className="users-orgs-user-detail-actions">
                      <button
                        type="submit"
                        className="users-orgs-user-detail-save-btn"
                        disabled={
                          !(
                            String(inviteUserModalForm.firstName || '').trim() ||
                            String(inviteUserModalForm.lastName || '').trim() ||
                            String(inviteUserModalForm.email || '').trim()
                          )
                        }
                      >
                        Invite
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {addOrganizationModalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAddOrganizationModal();
          }}
        >
          <div
            className="assets-detail-modal users-orgs-org-detail-modal users-orgs-add-organization-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-orgs-add-organization-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id="users-orgs-add-organization-title">
                  Add organization
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeAddOrganizationModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form className="users-orgs-org-detail-form" onSubmit={submitAddOrganizationModal}>
                <div className="users-orgs-add-organization-scroll">
                {addOrganizationModalError ? (
                  <div
                    className="profile-settings-message profile-settings-error"
                    role="alert"
                    style={{ marginBottom: 12 }}
                  >
                    {addOrganizationModalError}
                  </div>
                ) : null}
                <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-add-org-details-heading">
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="users-orgs-add-org-details-heading" className="users-orgs-add-invite-card-title">
                      Organization details
                    </h3>
                    <p className="users-orgs-org-assign-hint">Name, description, and parent organization.</p>
                  </div>
                  <div className="users-orgs-add-invite-card-body">
                    <div className="users-orgs-org-detail-field">
                      <label htmlFor={addOrganizationNameId}>Organization name</label>
                      <input
                        id={addOrganizationNameId}
                        type="text"
                        className="users-orgs-org-detail-input"
                        value={addOrganizationForm.name}
                        onChange={(e) => setAddOrganizationForm((f) => ({ ...f, name: e.target.value }))}
                        autoComplete="organization"
                        disabled={addOrganizationSubmitting}
                        placeholder="Name"
                      />
                    </div>
                    <div className="users-orgs-org-detail-field">
                      <label htmlFor={addOrganizationDescId}>Description</label>
                      <input
                        id={addOrganizationDescId}
                        type="text"
                        className="users-orgs-org-detail-input"
                        value={addOrganizationForm.description}
                        onChange={(e) => setAddOrganizationForm((f) => ({ ...f, description: e.target.value }))}
                        disabled={addOrganizationSubmitting}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="users-orgs-org-detail-field">
                      <label htmlFor={addOrganizationManagedById}>Managed by</label>
                      <select
                        id={addOrganizationManagedById}
                        className="users-orgs-org-detail-select"
                        value={addOrganizationForm.managedBy}
                        onChange={(e) => setAddOrganizationForm((f) => ({ ...f, managedBy: e.target.value }))}
                        disabled={addOrganizationSubmitting}
                      >
                        <option value="">Not managed by another organization</option>
                        {managedByOrganizationOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>
                <section className="users-orgs-add-invite-card" aria-labelledby={addOrganizationUsersId}>
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id={addOrganizationUsersId} className="users-orgs-add-invite-card-title">
                      Assign users
                    </h3>
                    <p className="users-orgs-org-assign-hint">
                      Selected users get access to this organization.
                    </p>
                  </div>
                  <div className="users-orgs-add-invite-card-body">
                    <input
                      type="search"
                      className="users-orgs-org-detail-input users-orgs-org-assign-search"
                      value={addOrganizationUserSearch}
                      onChange={(e) => setAddOrganizationUserSearch(e.target.value)}
                      placeholder="Search users"
                      aria-label="Search users"
                      disabled={addOrganizationSubmitting}
                      autoComplete="off"
                    />
                    <div className="users-orgs-org-assign-list" role="group" aria-labelledby={addOrganizationUsersId}>
                      {allUsersRows.length === 0 ? (
                        <p className="users-orgs-org-assign-empty">No users available to assign.</p>
                      ) : addOrganizationUserMatches.length === 0 ? (
                        <p className="users-orgs-org-assign-empty">No users match that search.</p>
                      ) : (
                        addOrganizationUserMatches.map((user) => {
                          const userKey = String(user.userId || user.id);
                          const checked = (addOrganizationForm.assignedUserIds || []).includes(userKey);
                          return (
                            <label key={userKey} className="users-orgs-org-assign-option">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={addOrganizationSubmitting}
                                onChange={(e) => {
                                  const nextChecked = e.target.checked;
                                  setAddOrganizationForm((current) => {
                                    const currentIds = Array.isArray(current.assignedUserIds) ? current.assignedUserIds : [];
                                    const assignedUserIds = nextChecked
                                      ? Array.from(new Set([...currentIds, userKey]))
                                      : currentIds.filter((id) => id !== userKey);
                                    return { ...current, assignedUserIds };
                                  });
                                }}
                              />
                              <span>
                                <strong>{user.name || user.email}</strong>
                                {user.email && user.name ? <span>{user.email}</span> : null}
                                {user.allOrganizationsAccess ? <span>Already has access to all organizations</span> : null}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </section>
                <section className="users-orgs-add-invite-card" aria-labelledby="users-orgs-add-org-options-heading">
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="users-orgs-add-org-options-heading" className="users-orgs-add-invite-card-title">
                      Options
                    </h3>
                    <p className="users-orgs-org-assign-hint">Default scan and site settings for this organization.</p>
                  </div>
                  <div className="users-orgs-add-invite-card-body">
                    <div className="users-orgs-org-detail-check-row">
                      <input
                        id={addOrganizationCbEcommerceId}
                        className="users-orgs-org-detail-check-input"
                        type="checkbox"
                        checked={Boolean(addOrganizationForm.ecommerceMode)}
                        onChange={(e) =>
                          setAddOrganizationForm((f) => ({ ...f, ecommerceMode: e.target.checked }))
                        }
                        disabled={addOrganizationSubmitting}
                      />
                      <label className="users-orgs-org-detail-check-label" htmlFor={addOrganizationCbEcommerceId}>
                        eCommerce Mode
                      </label>
                    </div>
                    <div className="users-orgs-org-detail-check-row">
                      <input
                        id={addOrganizationCbWebScanId}
                        className="users-orgs-org-detail-check-input"
                        type="checkbox"
                        checked={Boolean(addOrganizationForm.webScanEnabled)}
                        onChange={(e) =>
                          setAddOrganizationForm((f) => ({ ...f, webScanEnabled: e.target.checked }))
                        }
                        disabled={addOrganizationSubmitting}
                      />
                      <label className="users-orgs-org-detail-check-label" htmlFor={addOrganizationCbWebScanId}>
                        Web Application Scan Enabled
                      </label>
                    </div>
                    <div className="users-orgs-org-detail-check-row">
                      <input
                        id={addOrganizationCbPerimeterId}
                        className="users-orgs-org-detail-check-input"
                        type="checkbox"
                        checked={Boolean(addOrganizationForm.perimeterWithScanner)}
                        onChange={(e) =>
                          setAddOrganizationForm((f) => ({ ...f, perimeterWithScanner: e.target.checked }))
                        }
                        disabled={addOrganizationSubmitting}
                      />
                      <label className="users-orgs-org-detail-check-label" htmlFor={addOrganizationCbPerimeterId}>
                        Create with Perimeter site and with scanner assigned
                      </label>
                    </div>
                  </div>
                </section>
                </div>
                <div className="users-orgs-org-detail-actions">
                  <button
                    type="submit"
                    className="users-orgs-org-detail-save-btn"
                    disabled={addOrganizationSubmitting || !String(addOrganizationForm.name || '').trim()}
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {bulkUserDeleteKeys?.length ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeBulkUserDeleteModal();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete selected users"
            aria-describedby="users-orgs-bulk-user-delete-desc"
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeBulkUserDeleteModal}
              aria-label="Close"
            >
              ✕
            </button>
            <p id="users-orgs-bulk-user-delete-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete{' '}
              <strong className="assets-exclude-confirm-name">{bulkUserDeleteKeys.length}</strong>{' '}
              selected users.
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmBulkDeleteUsers}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkOrgDeleteIds?.length ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeBulkOrgDeleteModal();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete selected organizations"
            aria-describedby={`users-orgs-bulk-org-delete-desc ${bulkOrgDeletePasswordHintId}`}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeBulkOrgDeleteModal}
              aria-label="Close"
            >
              ✕
            </button>
            <p id="users-orgs-bulk-org-delete-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete{' '}
              <strong className="assets-exclude-confirm-name">{bulkOrgDeleteIds.length}</strong>{' '}
              selected organizations. Related users and sites will be removed from the list.
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="users-orgs-delete-confirm-password-block">
              <label htmlFor={bulkOrgDeletePasswordConfirmId} className="users-orgs-delete-confirm-password-label">
                Enter your account password to confirm
              </label>
              <input
                id={bulkOrgDeletePasswordConfirmId}
                type="password"
                className="users-orgs-delete-confirm-password-input"
                autoComplete="current-password"
                value={bulkOrgDeletePasswordConfirm}
                onChange={(e) => {
                  setBulkOrgDeletePasswordConfirm(e.target.value);
                  setBulkOrgDeleteAuthError('');
                }}
                aria-describedby={bulkOrgDeletePasswordHintId}
              />
              <span id={bulkOrgDeletePasswordHintId} className="edi-sr-only">
                Password must be at least {BULK_ORG_DELETE_PASSWORD_MIN_LEN} characters to enable Delete.
              </span>
            </div>
            {bulkOrgDeleteAuthError ? (
              <p className="users-orgs-delete-confirm-auth-error" role="alert">
                {bulkOrgDeleteAuthError}
              </p>
            ) : null}
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmBulkDeleteOrganizations}
                disabled={
                  bulkOrgDeleteVerifying ||
                  bulkOrgDeletePasswordConfirm.trim().length < BULK_ORG_DELETE_PASSWORD_MIN_LEN
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default UsersOrgsSettingsPage;
